import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Stack, Typography, IconButton, MenuItem, Menu,
  ListItemButton, ListItemText, Tabs, Tab, useTheme, useMediaQuery, Fab,
  Divider, SwipeableDrawer, Button, TextField, InputAdornment
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import Add from "@mui/icons-material/Add";
import AddCircleOutlineRounded from "@mui/icons-material/AddCircleOutlineRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import AccountBalance from "@mui/icons-material/AccountBalance";
import CloseRounded from "@mui/icons-material/CloseRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import VisibilityOffRounded from "@mui/icons-material/VisibilityOffRounded";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import SwapHorizRounded from "@mui/icons-material/SwapHorizRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ACCOUNTS as MOCK_ACCOUNTS, BOTS } from "@/data/mock-data";
import type { BrokerAccount } from "@/data/mock-data";
import { useAuthStore } from "@/store/auth-store";
import {
  useRealAccountsAdapted,
  useRealBrokerAccountDetailAdapted,
  useRealBotDetailsBulk,
  useRealLiveAccountsMerged,
  useRealAccountPositions,
  useRealLiveAccountPositions,
  useRealAccountOrders,
  useRealLiveAccountOrders,
  useRealAccountActivities,
  useRealLiveAccountActivities,
  useRealBotsForAccount,
  useRealBots,
  useArchivedBots,
  useRealHistoricalPrices,
} from "@/lib/whb-data";
import { mapRealActivitiesToActivities } from "@/lib/whb-accounts-adapter";
import type { RawAccountActivity } from "@/lib/whb-accounts-adapter";
import { ORDERS } from "@/data/mock-orders";
import {
  aggregateLiveAccounts,
  ALL_LIVE_ACCOUNT_ID,
  isAllLiveAccountId
} from "@/lib/aggregate-accounts";
import { formatUSD, formatPct } from "@/lib/formatters";
import {
  deriveAccountMetrics,
  deriveRangeScopedMetrics,
  formatWeeklyPct,
  maxRecoveryTime
} from "@/lib/account-metrics";
import { windowReturnDecimal, windowDollarReturn } from "@/lib/chart-math";
import { getMarketState } from "@/lib/market-status";
import { MetricsSettingsDialog } from "@/components/MetricsSettingsDialog";
import { useRefreshBus } from "@/store/refresh-bus-store";
import {
  DEFAULT_ORDER,
  useMetricsSettingsStore,
  type MetricCardId
} from "@/store/metrics-settings-store";
import { TERMS } from "@/lib/terms";
import type { Term } from "@/lib/terms";
import { DeltaBadge } from "@/components/DeltaBadge";
import { CreateMenu } from "@/components/CreateMenu";
import { MetricTip } from "@/components/MetricTip";
import { PortfolioVsSpyChart } from "@/components/PortfolioVsSpyChart";
import type { GraphMode } from "@/components/PortfolioVsSpyChart";
import {
  OrdersFilterBar,
  DEFAULT_ORDERS_FILTER,
  applyOrdersFilter
} from "@/components/OrdersFilterBar";
import type { OrdersFilterState } from "@/components/OrdersFilterBar";
import { OrdersSimpleView } from "@/components/OrdersSimpleView";
import { OrdersAdvancedView } from "@/components/OrdersAdvancedView";
import {
  BotsFilterBar,
  DEFAULT_BOTS_FILTER,
  applyBotsFilter
} from "@/components/BotsFilterBar";
import type { BotsFilterState } from "@/components/BotsFilterBar";
import { BotsSimpleView } from "@/components/BotsSimpleView";
import { BotsAdvancedView } from "@/components/BotsAdvancedView";
import { BotDetailDrawer } from "@/components/BotDetailDrawer";
import {
  PositionsFilterBar,
  DEFAULT_POSITIONS_FILTER,
  applyPositionsFilter
} from "@/components/PositionsFilterBar";
import type { PositionsFilterState } from "@/components/PositionsFilterBar";
import { PositionsSimpleView } from "@/components/PositionsSimpleView";
import { PositionsAdvancedView } from "@/components/PositionsAdvancedView";
import { PositionDetailDrawer } from "@/components/PositionDetailDrawer";
import {
  positionsForAccounts,
  mergePositionsByTicker
} from "@/data/mock-positions";
import type { Position } from "@/data/mock-positions";
import { BalancesView } from "@/components/BalancesView";
import {
  aggregateBalances as aggregateBalanceSnapshots,
  deriveBalance as deriveBalanceSnapshot
} from "@/data/mock-balances";
import {
  ActivitiesFilterBar,
  DEFAULT_ACTIVITIES_FILTER,
  applyActivitiesFilter
} from "@/components/ActivitiesFilterBar";
import type { ActivitiesFilterState } from "@/components/ActivitiesFilterBar";
import { ActivitiesSimpleView } from "@/components/ActivitiesSimpleView";
import { activitiesForAccounts } from "@/data/mock-activities";
import { SettingsView } from "@/components/SettingsView";
import { mobileFullWidthMenuPaperSx } from "@/theme/mobile-menu-sx";
import { desktopBottomDrawerPaperSx } from "@/theme/drawer-sx";
import { CachedDataPopover, MarketHoursIcon, FreshnessDot } from "@/components/MarketStatusIndicator";
import { RealSessionBadge } from "@/components/RealSessionBadge";
import { RealBrokerAccountsDebugPanel } from "@/components/RealBrokerAccountsDebugPanel";
import { useDevFlagsStore } from "@/store/dev-flags-store";

/**
 * Accounts page — V2 (Robinhood-style) mobile layout.
 *
 * Differences from V1:
 *  • No TopAppBar on this route (MobileShellV2 hides it everywhere
 *    except /profile). That means the account selector becomes the
 *    page's own pinned header at top: 0.
 *  • Total Portfolio Value heading sits directly under the selector —
 *    no "TOTAL PORTFOLIO VALUE" overline (Sergey wanted the number to
 *    be the thing, not a labeled field).
 *  • The % change chip under the heading now plays the role the chart's
 *    "P" (Portfolio) legend chip played in V1 — hence the chart in V2
 *    drops its own P chip (see `PortfolioVsSpyChart` with `v2` prop).
 *  • Chart uses `v2` prop: no title row, taller, $/% toggle + eye move
 *    into the range chip row.
 *  • Buying Power sits just UNDER the chart (Sergey: "Move Buying Power
 *    under graph"). Kept as an inline toggle card for now — it's on the
 *    roadmap to become its own page later.
 *  • Tabs preserved (Metrics, Orders, Bots, Positions, Balances,
 *    Activities, Settings) — the page content below the hero is
 *    identical to V1.
 *
 * Desktop is untouched: this component is only reachable on mobile via
 * the V2 dispatch inside AccountsPage (and MobileShellV2 is mobile-only).
 */
export function AccountsPageV2() {
  const theme = useTheme();
  // Desktop hosts V2 inside DesktopShell, which keeps the TopAppBar
  // (Toolbar minHeight: 72) sticky at top:0. The selector inside V2
  // would otherwise stick at the very top of the viewport, going UNDER
  // the TopAppBar. Offset the sticky to clear it on desktop. Mobile is
  // unchanged (no TopAppBar on /accounts in MobileShellV2). Sergey
  // 2026-05-13.
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const STICKY_TOP_OFFSET = isDesktop ? 72 : 0;
  const navigate = useNavigate();

  // Pick the account source per auth kind. Demo sessions always render
  // the deterministic mock fixture. Real sessions render accounts
  // returned by /broker-accounts (adapted into the PWA's BrokerAccount
  // shape — identity fields are real, balance/curve fields are zeroed
  // until a balances endpoint is wired). If the real fetch hasn't
  // landed yet we fall back to the mock so the page isn't blank during
  // the first paint. Every downstream `ACCOUNTS` reference in this
  // component body reads from `ACCOUNTS` below, so the swap is
  // transparent to the existing layout code.
  const authKind = useAuthStore((s) => s.kind);
  const { accounts: realAccounts } = useRealAccountsAdapted();
  const isRealSession = authKind === "real";
  // Debug panel is dev-only and gated behind a Settings → Developer
  // toggle; defaults OFF so production users don't see it.
  const debugPanelEnabled = useDevFlagsStore((s) => s.debugPanelEnabled);
  const ACCOUNTS_RAW: BrokerAccount[] =
    isRealSession && realAccounts && realAccounts.length > 0
      ? realAccounts
      : MOCK_ACCOUNTS;

  // For real sessions, fire `/broker-accounts/{id}` in parallel for
  // every live account and merge the detail overlay onto each row.
  // Without this:
  //  • the selector dropdown shows $0 for every live row (the list
  //    endpoint has no portfolio data),
  //  • `aggregateLiveAccounts` sums a pile of zeros, so the "All
  //    Live Accounts" hero renders at $0 with flat curves.
  // Every downstream use of `ACCOUNTS` in this component reads from
  // the hydrated list so the selector, hero, aggregate, and tabs all
  // see the same real numbers. Mock accounts + paper rows pass
  // through unchanged.
  const { accounts: ACCOUNTS } = useRealLiveAccountsMerged(ACCOUNTS_RAW);

  // Virtual "All Live Accounts" aggregate. Computed once per render so
  // the selector, the hero, and every tab below share a single
  // consolidated object. Returns `null` when no live accounts exist —
  // in that case we hide the All-Live selector entry and skip the
  // aggregate code paths.
  const liveAggregate = useMemo(
    () => aggregateLiveAccounts(ACCOUNTS),
    [ACCOUNTS]
  );
  const isValidAccountId = useCallback(
    (id: string) =>
      ACCOUNTS.some((a) => a.id === id) ||
      (id === ALL_LIVE_ACCOUNT_ID && !!liveAggregate),
    [ACCOUNTS, liveAggregate]
  );

  // `?account=<id>` is the source of truth so external links and
  // intra-app navigation can switch the active account on this page.
  const [searchParams, setSearchParams] = useSearchParams();
  const queryAccountId = searchParams.get("account");

  // Default-selection precedence:
  //   1. `?account=<id>` URL param (deep-links, intra-app nav)
  //   2. Last account the user picked (persisted in localStorage)
  //   3. The "All Live Accounts" virtual aggregate, if any live exist
  //   4. First account in the list (ultimate fallback)
  //
  // (3) ensures a fresh user lands on a consolidated view instead of
  //  whichever account happens to sort first alphabetically — Sergey's
  //  feedback 2026-04-21.
  const LAST_ACCOUNT_KEY = "whb.accounts.lastId";
  const readLastAccountId = (): string | null => {
    try {
      return typeof window !== "undefined"
        ? window.localStorage.getItem(LAST_ACCOUNT_KEY)
        : null;
    } catch {
      return null;
    }
  };
  const persistedAccountId = readLastAccountId();
  const initialAccountId =
    queryAccountId && isValidAccountId(queryAccountId)
      ? queryAccountId
      : persistedAccountId && isValidAccountId(persistedAccountId)
      ? persistedAccountId
      : liveAggregate
      ? ALL_LIVE_ACCOUNT_ID
      : ACCOUNTS[0]!.id;

  const [accountId, setAccountId] = useState<string>(initialAccountId);
  const [selectorAnchor, setSelectorAnchor] = useState<HTMLElement | null>(null);
  // Selector search query — only surfaced (and only useful) when the
  // user has more than ~7 accounts; with a handful of accounts, scroll
  // beats search. State lives at the page level so it can reset to ""
  // every time the menu re-opens. Sergey 2026-04-27.
  const [selectorSearch, setSelectorSearch] = useState("");
  const [createAnchor, setCreateAnchor] = useState<HTMLElement | null>(null);

  // Chart hide/show — owned by the page in V2 so the toggle eye can
  // live next to the hero delta row (a visual sibling of the Total
  // Portfolio readout), not tucked inside the chart's legend. When
  // hidden the chart's whole block is simply not rendered below; the
  // eye flips to the "show" glyph so users can bring it back.
  const [chartHidden, setChartHidden] = useState(false);

  // Chart mode is LIFTED out of the chart so the hero row's delta
  // chip (+%) and $ change chip can act as quick switches into each
  // mode. Per Apr-2026 feedback the inline $/% toggle in the chart
  // range row was removed; tapping the +% chip forces pct, tapping
  // the +$ chip forces usd, and the canonical enable/disable surface
  // is the Graph Settings dialog inside the chart. Default "pct"
  // matches the chart's historical default.
  const [mode, setMode] = useState<GraphMode>("pct");

  // Hover-reactive hero state. When the user moves the crosshair across
  // the chart, the Total Portfolio value + delta chip + $ change below
  // show the values at that moment in time; when the cursor leaves the
  // chart, these fall back to the account's snapshot ("now"). Populated
  // by `PortfolioVsSpyChart`'s `onHoverState` callback.
  //
  // Using `useCallback` keeps the callback identity stable between
  // renders so the chart's emit-effect doesn't re-fire unnecessarily.
  const [hoverState, setHoverState] = useState<null | {
    idx: number;
    usd: number;
    pctChange: number;
    usdChange: number;
    dateLabel: string;
    isHover: boolean;
  }>(null);
  const handleHoverState = useCallback((s: typeof hoverState) => {
    setHoverState(s);
  }, []);

  // Robinhood-style "Past week" / "All time" / "in range" tagline that
  // sits after the $ number when the user ISN'T hovering the chart.
  // Populated by `PortfolioVsSpyChart`'s `onRangeChange` callback —
  // fires on mount + whenever the user taps a range chip or picks a
  // custom date range. Default "All time" matches the chart's default
  // ALL range so the first paint has a correct label before the chart
  // has a chance to emit.
  const [rangeInfo, setRangeInfo] = useState<{
    key: string;
    label: string;
    customLabel: string | null;
    startIso: string;
    endIso: string;
  }>({ key: "ALL", label: "All time", customLabel: null, startIso: "", endIso: "" });
  const handleRangeChange = useCallback(
    (info: {
      key: string;
      label: string;
      customLabel: string | null;
      startIso: string;
      endIso: string;
    }) => {
      setRangeInfo({
        key: info.key,
        label: info.label,
        customLabel: info.customLabel,
        startIso: info.startIso,
        endIso: info.endIso
      });
    },
    []
  );

  // Shared last-quote-update timestamp for the hero's freshness UI.
  // Both `CachedDataPopover` (click-to-open card on the hero $) and
  // `FreshnessDot` (superscript index next to the digits) derive their
  // green/yellow state from this single value. Starting as `undefined`
  // so both components fall back to the deterministic mock clock
  // (`getMockLastUpdate`) until the user presses Update. When Update
  // fires, we stamp `new Date()` here; the popover and dot re-render
  // and the freshness rule flips both to "fresh" simultaneously
  // (Apr-2026 R12-5b: "when updated the yellow dot becomes green").
  const [lastQuoteUpdate, setLastQuoteUpdate] = useState<Date | undefined>(undefined);
  const handleQuoteUpdate = useCallback(() => {
    setLastQuoteUpdate(new Date());
  }, []);

  // Listen for T1 refresh requests on the shared refresh-bus — the
  // idle modal's Refresh button, the 15-min interval, focus-regained,
  // and `online`-event triggers all funnel through this. For now the
  // handler just stamps the freshness timestamp (so the hero dot
  // flips green and consumers re-render). A future pass will chain
  // actual `useRealBrokerAccountDetail` / `useRealAccountOrders`
  // refetches off the same token.
  const refreshBusToken = useRefreshBus((s) => s.t1Token);
  useEffect(() => {
    // Skip the initial mount (token=0). Only stamp on real bumps.
    if (refreshBusToken > 0) {
      handleQuoteUpdate();
    }
  }, [refreshBusToken, handleQuoteUpdate]);

  useEffect(() => {
    if (
      queryAccountId &&
      queryAccountId !== accountId &&
      isValidAccountId(queryAccountId)
    ) {
      setAccountId(queryAccountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryAccountId]);

  // When the accounts source flips (e.g. real broker-accounts land
  // after demo-mounted with a mock id, or the user signs out back to
  // demo and the persisted accountId no longer exists), re-anchor.
  // Use the same precedence as first-mount: All-Live aggregate first,
  // fall back to ACCOUNTS[0]. Do NOT re-read localStorage here — if
  // the persisted id was invalid on mount we're already past that
  // branch and should not bounce the user back to it.
  useEffect(() => {
    if (!ACCOUNTS.length) return;
    const stillValid =
      ACCOUNTS.some((a) => a.id === accountId) ||
      (isAllLiveAccountId(accountId) && !!liveAggregate);
    if (!stillValid) {
      setAccountId(liveAggregate ? ALL_LIVE_ACCOUNT_ID : ACCOUNTS[0]!.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ACCOUNTS, liveAggregate]);

  // Scroll to top on account change. Same behaviour as V1 — the switch
  // can come from the selector below or from a deep-link, and both
  // paths eventually mutate `accountId`.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [accountId]);

  const selectAccount = (id: string) => {
    setAccountId(id);
    // Persist so the next visit lands back on the same account/aggregate.
    // Wrapped because some private-mode browsers throw on setItem.
    try {
      window.localStorage.setItem(LAST_ACCOUNT_KEY, id);
    } catch {
      /* storage disabled — fine, we just won't restore next time */
    }
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("account", id);
        return p;
      },
      { replace: true }
    );
    setSelectorAnchor(null);
    setSelectorSearch("");
  };

  // Resolve the active account. Three branches:
  //  • aggregate id + aggregate exists → use the virtual account.
  //  • real account id → look up in ACCOUNTS.
  //  • fallback → first real account.
  const baseAccount: BrokerAccount =
    isAllLiveAccountId(accountId) && liveAggregate
      ? liveAggregate
      : (ACCOUNTS.find((a) => a.id === accountId) ?? ACCOUNTS[0]!);
  const isAggregateView = isAllLiveAccountId(baseAccount.id);

  // Per-account detail fetch — fires only when the user is on a real
  // session AND has a specific (non-aggregate, non-mock) account
  // selected. The hook idles for mock accounts, for the All-Live
  // aggregate, and for demo sessions so we never hit the proxy under
  // a context that would 404 or 401. When the detail lands we merge
  // it on top of the list-row shape so the hero has real numbers +
  // equity curve + positions + bot ids populated.
  const detailFetchId =
    isRealSession && !isAggregateView && baseAccount.source === "real"
      ? baseAccount.id
      : null;
  const { overlay: detailOverlay } =
    useRealBrokerAccountDetailAdapted(detailFetchId);
  const account: BrokerAccount = useMemo(() => {
    if (!detailOverlay) return baseAccount;
    // Merge: the detail endpoint only populates hero + curve + bot
    // fields (see mapRealBrokerAccountDetail). Identity fields —
    // name, provider ("Alpaca"/"WhiteHatBots"), masked, paper —
    // come from the list row because the detail response has no
    // `displayName` to extract the broker label from.
    return { ...baseAccount, ...detailOverlay };
  }, [baseAccount, detailOverlay]);
  // Real-session bots come from `GET /bots` filtered to the subset
  // attached to the current account (via `account.botIds` populated by
  // the detail adapter). Demo sessions and mock accounts fall back to
  // the seeded BOTS list. `useRealBotsForAccount` returns null until
  // the /bots fetch lands (or immediately for demo / non-real rows);
  // we render nothing from that null and drop to the mock filter below.
  const realBots = useRealBotsForAccount(account);
  const bots = useMemo(
    () =>
      realBots ?? BOTS.filter((b) => account.botIds.includes(b.id)),
    [realBots, account.botIds]
  );

  // Split ACCOUNTS into Live / Paper once for the selector menu. Both
  // lists preserve ACCOUNTS' in-source ordering so the top-most live
  // and top-most paper entries are deterministic.
  const liveAccounts = ACCOUNTS.filter((a) => !a.paper);
  const paperAccounts = ACCOUNTS.filter((a) => a.paper);

  return (
    <Box
      sx={{
        // Side-by-side split-pane layout (xl+, 1536px+). The whole
        // page area becomes a fixed-height container: page itself does
        // NOT scroll, only the right (tabs) column scrolls internally.
        // The TopAppBar stays put (it's outside this Box) and the left
        // (chart) column stays put too.
        //
        // Height calc (Sergey 2026-05-13 — "make space available up to
        // the page fold"):
        //   - 100vh is the browser viewport height.
        //   - Subtract 72 for the sticky TopAppBar and 32 for main's
        //     py-top — those are above us and we can't absorb them.
        //   - DON'T subtract main's py-bottom (32px); instead use a
        //     negative `mb: -4` so this Box extends past main's
        //     bottom padding all the way to the viewport fold.
        // Net: container reaches the actual bottom of the visible
        // page, so list rows / cards stop at the fold rather than
        // leaving a 32px empty band above the OS chrome.
        maxWidth: { xs: 680, xl: 1400 },
        mx: "auto",
        position: "relative",
        px: 2,
        display: { xl: "grid" },
        gridTemplateColumns: { xl: "680px 1fr" },
        gridTemplateAreas: { xl: '"chart tabs"' },
        columnGap: { xl: 4 },
        alignItems: { xl: "stretch" },
        height: { xl: "calc(100vh - 104px)" },
        mb: { xl: -4 },
        overflow: { xl: "hidden" },
      }}
    >
      {/* ---- Left column: chart hero + buying power ----
       *  On wide desktop (xl) the left column is locked in place: it
       *  fills the full pane height, hides overflow horizontally, and
       *  scrolls internally on Y only as a fallback for short
       *  viewports. The chart hero, selector, and Buying Power row are
       *  designed to fit within ~600px of vertical space, so on any
       *  normal-height desktop the rail simply doesn't scroll.
       *
       *  `overflowX: hidden` is critical: setting overflow-y while
       *  leaving overflow-x at default `visible` promotes BOTH to auto
       *  in some browsers and produces a phantom horizontal scrollbar
       *  whenever a child (chart svg, range chip strip) is even 1px
       *  wider than 680. Forcing overflow-x hidden suppresses it. */}
      <Box
        sx={{
          gridArea: { xl: "chart" },
          minWidth: 0,
          height: { xl: "100%" },
          overflowY: { xl: "auto" },
          overflowX: { xl: "hidden" },
          // Slim, mostly-invisible scrollbar in the left rail for the
          // rare case where content does spill (short viewport). The
          // rail is a glanceable summary, not a long-form list.
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "action.disabled",
            borderRadius: 3,
          },
        }}
      >
      {/* Sticky account selector — pinned to the top of the viewport in
       *  V2 because MobileShellV2 renders no TopAppBar on this route.
       *  The bg + negative margin trick lets the selector extend
       *  edge-to-edge when the user scrolls the chart underneath it.
       *  On wide desktop the parent left column is itself sticky, so
       *  the selector just flows at the top of the column (the column
       *  pins to the viewport, which carries the selector with it). */}
      <Box
        sx={{
          // On wide desktop (xl+) the left column itself is sticky —
          // an inner sticky here would compete and produce confusing
          // jitter as the user scrolls. Use `static` on xl so the
          // selector simply flows at the top of the (sticky) column.
          position: { xs: "sticky", xl: "static" },
          top: STICKY_TOP_OFFSET,
          zIndex: (t) => t.zIndex.appBar,
          bgcolor: "background.default",
          // Negative margin extends the selector to the outer Box edges
          // in single-column mode. On wide-grid we don't want that —
          // the bleed would leak into the column gap. Use 0 there.
          mx: { xs: -2, xl: 0 },
          px: { xs: 2, xl: 0 },
          py: 1,
          // Hairline underline only when scrolled so the header blends
          // into the page at rest. CSS-only; no scroll listener.
          boxShadow: "none"
        }}
      >
        <Box sx={{ position: "relative" }}>
          <ListItemButton
            onClick={(e) => setSelectorAnchor(e.currentTarget)}
            sx={{
              bgcolor: "background.paper",
              px: 2,
              py: 1.25,
              borderRadius: 3
            }}
          >
            <AccountBalance sx={{ mr: 1.5, color: "text.secondary", flexShrink: 0 }} />
            <ListItemText
              sx={{ minWidth: 0, my: 0, pr: 4 }}
              primary={
                <Typography fontWeight={700} noWrap>
                  {account.name}
                  {account.masked ? ` ${account.masked}` : ""}
                </Typography>
              }
              secondary={
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  component="span"
                  sx={{ display: "block" }}
                >
                  {/* Aggregate: "3 live accounts · $174,680"
                   *  Real live:  "Alpaca"
                   *  Real paper: "Alpaca Paper · Paper" */}
                  {isAggregateView
                    ? `${account.provider} · ${formatUSD(account.portfolioValue, { maximumFractionDigits: 0 })}`
                    : `${account.provider}${account.paper ? " · Paper" : ""}`}
                </Typography>
              }
            />
            <KeyboardArrowDown sx={{ flexShrink: 0 }} />
          </ListItemButton>
          {/* V2 has no TopAppBar on /accounts, so the header badge needs
           *  to live here for real-session users on mobile. Absolutely
           *  positioned so it doesn't disturb the ListItemButton flex
           *  layout; compact icon-only variant keeps it out of the way
           *  of the account name. Renders nothing for demo sessions. */}
          <Box
            sx={{
              position: "absolute",
              top: 6,
              right: 36,
              pointerEvents: "none"
            }}
          >
            <RealSessionBadge compact />
          </Box>
        </Box>
      </Box>

      {/* Real-account data probe. Hidden by default; user opts in via
       *  Settings → Developer → Debug panel. Component self-gates on
       *  kind === "real", so flipping the flag under demo is harmless. */}
      {isRealSession && debugPanelEnabled && <RealBrokerAccountsDebugPanel />}

      <Menu
        open={!!selectorAnchor}
        anchorEl={selectorAnchor}
        onClose={() => {
          setSelectorAnchor(null);
          setSelectorSearch("");
        }}
        slotProps={{
          paper: {
            sx: [
              { borderRadius: 3, mt: 1, minWidth: 320, maxHeight: "70vh" },
              mobileFullWidthMenuPaperSx
            ]
          }
        }}
      >
        {/* Search field — pinned at the top of the dropdown, always
         *  visible (Sergey 2026-04-27 — same UX as common account
         *  pickers). Sticky so it remains visible as the list
         *  scrolls. `onKeyDown` stops propagation so MUI's Menu
         *  doesn't grab letters / arrow keys while the user types. */}
        <Box
          key="search-field"
          sx={{
            px: 1.5,
            py: 1,
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: "background.paper"
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            autoFocus={
              typeof window !== "undefined" &&
              window.matchMedia("(pointer: fine)").matches
            }
            fullWidth
            size="small"
            placeholder="Search accounts"
            value={selectorSearch}
            onChange={(e) => setSelectorSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded
                    sx={{ fontSize: 18, color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
              endAdornment: selectorSearch ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSelectorSearch("")}
                    sx={{ p: 0.25 }}
                  >
                    <CloseRounded sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
              sx: {
                borderRadius: 2,
                bgcolor: (t) => t.palette.action.hover,
                "& fieldset": { border: "none" }
              }
            }}
          />
        </Box>

        {/* "+ Add new account" — pinned just under the search field,
         *  always visible regardless of search filter. Routes to the
         *  PWA's own `/accounts/new` page (Sergey is building the
         *  in-app workflow there). Sergey 2026-04-27. */}
        <MenuItem
          key="add-new"
          onClick={() => {
            navigate("/accounts/new");
            setSelectorAnchor(null);
            setSelectorSearch("");
          }}
        >
          <AddCircleOutlineRounded
            sx={{ mr: 1.25, color: "primary.main", fontSize: 20 }}
          />
          <Typography fontWeight={600} sx={{ color: "primary.main" }}>
            Add new account
          </Typography>
        </MenuItem>

        {(() => {
          // Filter live + paper sections by the search query — case
          // insensitive, matched against name + provider + masked id
          // so "alpaca", "9027", or "ibkr" all hit the right rows.
          // Universal — same filter for demo and real, no source
          // branches.
          const q = selectorSearch.trim().toLowerCase();
          const matches = (a: BrokerAccount) => {
            if (!q) return true;
            const haystack = `${a.name} ${a.provider} ${a.masked}`.toLowerCase();
            return haystack.includes(q);
          };
          const liveMatches = liveAccounts.filter(matches);
          const paperMatches = paperAccounts.filter(matches);
          // Aggregate row: matches "all", "live", "consolidated"
          const aggregateMatches =
            !q ||
            "all live accounts consolidated".includes(q) ||
            (liveAggregate ? false : false);
          const liveSectionVisible =
            liveMatches.length > 0 || (aggregateMatches && liveAggregate);
          const paperSectionVisible = paperMatches.length > 0;
          if (q && !liveSectionVisible && !paperSectionVisible) {
            return (
              <Box sx={{ px: 2, py: 2, color: "text.secondary" }}>
                <Typography variant="body2">
                  No accounts match "{selectorSearch}".
                </Typography>
              </Box>
            );
          }
          return (
            <>
              {liveSectionVisible && (
                <SectionHeader key="live-header">Live accounts</SectionHeader>
              )}
              {liveAggregate && aggregateMatches && (
                <MenuItem
                  key={ALL_LIVE_ACCOUNT_ID}
                  selected={isAllLiveAccountId(accountId)}
                  onClick={() => selectAccount(ALL_LIVE_ACCOUNT_ID)}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>
                      All Live Accounts
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      component="div"
                    >
                      Consolidated across {liveAccounts.length}{" "}
                      {liveAccounts.length === 1 ? "account" : "accounts"} ·{" "}
                      {formatUSD(liveAggregate.portfolioValue, {
                        maximumFractionDigits: 0
                      })}
                    </Typography>
                  </Box>
                </MenuItem>
              )}
              {liveMatches.map((a) => (
                <MenuItem
                  key={a.id}
                  selected={a.id === accountId}
                  onClick={() => selectAccount(a.id)}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={600} noWrap>
                      {a.name} {a.masked}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      component="div"
                    >
                      {/* Real rows only get balance data after the per-
                       *  account detail fetch fires (currently only for
                       *  the selected account). Hide the "$0" placeholder
                       *  on unknown balances so the dropdown doesn't
                       *  show every account as zero. */}
                      {a.provider}
                      {a.source === "real" && a.portfolioValue === 0
                        ? ""
                        : ` · ${formatUSD(a.portfolioValue, { maximumFractionDigits: 0 })}`}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}

              {liveSectionVisible && paperSectionVisible && (
                <Divider sx={{ my: 0.5 }} />
              )}

              {paperSectionVisible && (
                <SectionHeader key="paper-header">Paper accounts</SectionHeader>
              )}
              {paperMatches.map((a) => (
                <MenuItem
                  key={a.id}
                  selected={a.id === accountId}
                  onClick={() => selectAccount(a.id)}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={600} noWrap>
                      {a.name} {a.masked}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      component="div"
                    >
                      {a.provider}
                      {a.source === "real" && a.portfolioValue === 0
                        ? ""
                        : ` · ${formatUSD(a.portfolioValue, { maximumFractionDigits: 0 })}`}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </>
          );
        })()}
      </Menu>

      <CreateMenu
        anchor={createAnchor}
        onClose={() => setCreateAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      />

      {/* Hero block — no "Total Portfolio Value" overline (Sergey asked
       *  for the number to be the thing, not a labelled field). The %
       *  change chip below the number takes over the role the chart's
       *  "P" (Portfolio) legend chip used to serve.
       *
       *  Hover-reactive: when the crosshair is over the chart, the big
       *  number shows the portfolio value at that date, and the delta
       *  chip + $ change reflect the change from the start of the
       *  visible range (W / 1M / …) to the hovered date. Off-chart
       *  falls back to the account's snapshot values. */}
      {(() => {
        // All three hero readouts are derived together so they stay
        // coherent — never mix "snapshot dollar value" with "hover pct"
        // or vice versa. Kept inside an IIFE just to contain the locals.
        //
        // The chart fires `onHoverState` at `activeIdx = hoverIdx ?? last`
        // (CHART-SPEC) — i.e. it emits the window-end snapshot even
        // when the user isn't hovering, so the hero number + delta
        // chips track the ACTIVE RANGE. Pre-R12-10 we fell back to
        // `account.allTime*` on the resting state, which made ALL and
        // custom ranges render identical +$ / +% (Sergey's Apr-2026
        // screenshot). Fallback to the snapshot remains only for the
        // first paint before the chart has emitted.
        // Hero Total Value: show the API-reported snapshot at rest so
        // the number stays consistent across range changes — only swap
        // to the chart-emitted `usd` when the user is actively hovering
        // a specific date. Previously the hero tracked the chart's
        // reconstructed end-of-window value, which differs from
        // `account.portfolioValue` (API's true "today" figure) by any
        // correction/redistribution the reconstruction applied, so
        // toggling ranges made the Total Value wobble between the two
        // (Sergey Apr-2026: "default Total value shows ~$146k, but
        // when switched to other any date range... $139k").
        const heroUsd =
          hoverState?.isHover && hoverState?.usd != null
            ? hoverState.usd
            : account.portfolioValue;
        const heroPct = hoverState?.pctChange ?? account.allTimeChangePct;
        const heroUsdChange = hoverState?.usdChange ?? account.allTimeChange;
        // Tagline that sits to the right of the big $ number. ALWAYS
        // the Robinhood-style "Past week" / "All time" / "in range"
        // reflecting the active chart range — NEVER the hovered
        // crosshair date. Previously we swapped to the date while
        // hovering, but the chart already shows the date in its
        // centered slot below the chips, so the hero swap produced
        // duplicate info. Per Apr-2026 feedback ("when individual date
        // on graph selected do not show the date next to chips"), keep
        // the range label stable on the hero row.
        const heroTagline = rangeInfo.label;
        return (
          <Box sx={{ mt: 1 }}>
            {/* Hero figure row — Total with its freshness index dot on
             *  the left, Moon/Sun market-hours icon pinned to the right
             *  edge. `justifyContent: space-between` keeps the icon at
             *  the end of the line regardless of number length; the
             *  number group stays baseline-aligned so the small dot
             *  reads as a proper superscript on the digits. */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 0.5, gap: 1 }}
            >
              {/* Big $ number wrapped in a tap-to-open popover that
               *  surfaces the quote-freshness state (same copy the
               *  FreshnessDot shows in its tooltip) plus an "Update"
               *  button for the user to request a refresh. Per Apr-2026
               *  R12 feedback: "click on Portfolio Total Value should
               *  show cached-data tooltip". The superscript FreshnessDot
               *  stays inside the popover's trigger so tapping either
               *  the digits or the dot opens the same surface.
               *
               *  ⚠️ DO NOT change the Typography below from `variant="h1"`
               *  or set `component` to an inline tag (e.g. `span`).
               *  The hero number size IS the h1 theme value; wrapping an
               *  <h1> inside a span produced an invalid block-in-inline
               *  nesting that silently shrank the rendered size. The
               *  popover's wrapper is now a proper <div> so the <h1>
               *  here stays valid and renders at full hero size. */}
              <CachedDataPopover
                lastUpdate={lastQuoteUpdate}
                onUpdate={handleQuoteUpdate}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontFeatureSettings: '"tnum" 1',
                    lineHeight: 1.1
                  }}
                >
                  {formatUSD(heroUsd)}
                  <FreshnessDot lastUpdate={lastQuoteUpdate} />
                </Typography>
              </CachedDataPopover>
              {/* Right-cluster: market-hours icon alone. Row 1 used to
               *  also host the hide-chart eye; per Apr-2026 round-11
               *  feedback the eye has been moved down into the chip
               *  row (row 2) so the two right-edge icons (Market icon
               *  above + Eye below) form a vertical column aligned on
               *  the right edge of the hero.
               *  Pass the same `lastQuoteUpdate` down so the MarketHours
               *  tooltip (which also reads the freshness state via the
               *  shared `useMarketStatus` hook) stays in sync with the
               *  popover / dot. */}
              <MarketHoursIcon lastUpdate={lastQuoteUpdate} size={24} showLabel />
            </Stack>
            {/* Row 2 — delta chips + range tagline on the LEFT, hide/
             *  unhide chart eye pinned to the RIGHT edge so it sits
             *  directly under the MarketHoursIcon above (both on the
             *  hero's right gutter). `justifyContent: space-between`
             *  on the outer row is what achieves that column alignment;
             *  `ml: "auto"` alone isn't enough because the inner chip
             *  Stack uses `flexWrap="wrap"` and the auto-margin ended
             *  up placed inside the wrapped row instead of the outer
             *  row — per Apr-2026 R11 follow-up screenshot feedback
             *  ("hide/unhide graph icon must be inline verticality
             *  with market close/open icon"). */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 0.5, gap: 1 }}
            >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
              sx={{ rowGap: 0.5, flex: 1, minWidth: 0 }}
            >
              {/* Delta chip (+%) doubles as the "switch to Percent mode"
               *  control. The active mode is marked with a stroke
               *  (outlined border) per Apr-2026 feedback — inactive
               *  renders at full opacity with no stroke so the two
               *  chips both feel "live" but the active one reads as
               *  canonical via the border highlight. The $-change
               *  chip next to it is the Dollar-mode equivalent. */}
              <Box
                role="button"
                tabIndex={0}
                aria-pressed={mode === "pct"}
                aria-label="Show percent chart"
                onClick={() => setMode("pct")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setMode("pct");
                  }
                }}
                sx={{
                  display: "inline-flex",
                  cursor: "pointer",
                  borderRadius: 2,
                  px: 1,
                  py: 0.25,
                  // Apr-2026 round-9: active mode gets a green (primary)
                  // background wash; inactive stays transparent. This
                  // replaces the earlier stroke-border treatment, which
                  // Sergey judged too subtle on mobile where the chips
                  // sit inline with the range-label text.
                  bgcolor: mode === "pct"
                    ? (theme) => alpha(theme.palette.primary.main, 0.18)
                    : "transparent",
                  transition: "background-color 160ms ease",
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: 2
                  }
                }}
              >
                {/* `variant="plain"` — the DeltaBadge's own "chip" bg
                 *  would leak through the wrapper and paint the % chip
                 *  green even when $ mode is active. Let the wrapper's
                 *  bgcolor above be the sole source of the active
                 *  highlight; DeltaBadge renders just the arrow + %
                 *  text here. */}
                <DeltaBadge
                  value={heroPct}
                  size="lg"
                  variant="plain"
                />
              </Box>
              <Box
                role="button"
                tabIndex={0}
                aria-pressed={mode === "usd"}
                aria-label="Show dollar chart"
                onClick={() => setMode("usd")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setMode("usd");
                  }
                }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: "pointer",
                  borderRadius: 2,
                  px: 1,
                  py: 0.25,
                  // Mirror of the % chip's active treatment above —
                  // green background wash when this is the live mode.
                  bgcolor: mode === "usd"
                    ? (theme) => alpha(theme.palette.primary.main, 0.18)
                    : "transparent",
                  transition: "background-color 160ms ease",
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: 2
                  }
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: heroUsdChange >= 0 ? "primary.main" : "error.main",
                    fontFeatureSettings: '"tnum" 1'
                  }}
                >
                  {heroUsdChange >= 0 ? "+" : ""}{formatUSD(heroUsdChange)}
                </Typography>
              </Box>
              {/* Range tagline sits INLINE with the delta + $ change
               *  per Apr-2026 feedback ("should be in line with
               *  related chips") — they're all one row now, matching
               *  Robinhood's "+$230 (+2.34%) Past week" single-line
               *  layout. Always the range label ("Past week" / "All
               *  time" / "in range") — crosshair date lives ONLY in
               *  the chart's centered slot to avoid duplicate info.
               *  body1 so it reads at the same size as the "Buying
               *  Power" row below and feels first-class. */}
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontFeatureSettings: '"tnum" 1' }}
              >
                {heroTagline}
              </Typography>
            </Stack>
              {/* Hide/unhide chart eye — right-side sibling of the
               *  chip group above. Sits as the second child of the
               *  outer `space-between` Stack, pinning it to the hero's
               *  right gutter so it aligns vertically with the Market
               *  icon on row 1. Always visible (prior rev used an
               *  absolute overlay inside the chart — removed because
               *  the overlay covered chart content on small screens). */}
              <IconButton
                size="small"
                aria-label={chartHidden ? "Show chart" : "Hide chart"}
                onClick={() => setChartHidden((h) => !h)}
                sx={{
                  color: "text.secondary",
                  p: 0.5,
                  flexShrink: 0,
                  "&:hover": { color: "primary.main" }
                }}
              >
                {chartHidden
                  ? <VisibilityRounded sx={{ fontSize: 24 }} />
                  : <VisibilityOffRounded sx={{ fontSize: 24 }} />}
              </IconButton>
            </Stack>
          </Box>
        );
      })()}

      {/* Chart — V2 mode. Flush against the page gutter; the chart
       *  handles its own internal bleed. `onHoverState` pipes crosshair
       *  position back into the hero above. Gated on `!chartHidden`
       *  so the eye button above controls visibility without the chart
       *  needing its own internal hidden state.
       *
       *  `position: relative` on the wrapper lets us absolutely-
       *  position the hide-chart eye inside the chart's top-right
       *  corner (Apr-2026 feedback: the eye belongs INSIDE the chart
       *  it toggles, not up in the hero row). */}
      {!chartHidden && (
        // Bleed the chart + range-chip row edge-to-edge per round-5
        // feedback — the page has `px: 2` (16px gutter); we cancel it
        // with `mx: -2` so the chart body + the chip rail below it
        // both extend to the screen edges. The chart's own V2 Card
        // already has `p: 0` so there's no internal padding fighting
        // us. We *add back* a small `px` inside this block only
        // around the chip row (handled in the chart itself) — the
        // chart SVG stays full-bleed.
        //
        // Prior rev had an absolutely-positioned eye overlay inside
        // the chart's top-right corner. Removed in round-11: the
        // single source of truth for the hide/show eye is now the
        // chip-row IconButton above. One control, one place.
        <Box sx={{ mt: 0, mb: 2, mx: -2 }}>
          <PortfolioVsSpyChart
            account={account}
            compact
            v2
            mode={mode}
            onModeChange={setMode}
            onHoverState={handleHoverState}
            onRangeChange={handleRangeChange}
          />
        </Box>
      )}

      {/* Section divider — Apr-2026 feedback: chart and Buying Power
       *  had no visual break, making them read as one giant block. A
       *  thin divider + generous top margin mirrors Robinhood's
       *  section separation and tells the eye "new thing below".
       *
       *  When the chart is HIDDEN the hero row has no chart below it,
       *  so the tight `mt: 0` would push the divider straight against
       *  the delta chip — we add a top margin in that case so the
       *  spacing between the chips row and the Buying Power section
       *  reads as a normal section break. */}
      <Divider sx={{ mx: { xs: -2, xl: 0 }, mb: 2, mt: chartHidden ? 2 : 0 }} />

      {/* Buying Power — moved UNDER the chart per V2 spec. Kept as an
       *  inline toggle for now; the roadmap is to promote this to its
       *  own page. When that lands this whole block becomes a nav row
       *  (list row + arrow). */}
      <Box sx={{ mb: 3 }}>
        <BuyingPowerToggle
          account={account}
          bots={bots}
          isAggregate={isAggregateView}
          liveAccounts={ACCOUNTS.filter((a) => !a.paper)}
        />
      </Box>
      </Box>{/* ---- /Left column ---- */}

      {/* ---- Right column: tabs + tab content ----
       *  Plain block in both layouts. In wide-grid mode it lives in
       *  the `tabs` grid area and scrolls with the page; the left
       *  column above stays put as the user scrolls through Bots /
       *  Orders / Activities etc. In single-column (mobile/narrow)
       *  this is just the next block below the chart hero — identical
       *  to V2's original mobile shape. */}
      <Box
        sx={{
          gridArea: { xl: "tabs" },
          minWidth: 0,
          // On xl: flex column with NO scroll on the column itself.
          // The tabs Box (first child via AccountTabs's fragment) sits
          // at the top as a flexShrink:0 static element, and the panel
          // content Box (second child) takes `flex: 1` + overflowY:
          // auto so the scrollbar appears alongside the LIST rows
          // only, not running through the tabs strip / filter bar.
          // Sergey 2026-05-13: the scrollbar must live inside the
          // content area, not over the pinned chrome above it.
          height: { xl: "100%" },
          display: { xl: "flex" },
          flexDirection: { xl: "column" },
          overflow: { xl: "hidden" },
        }}
      >
      {/* Tabs — unchanged from V1 (Metrics, Orders, Bots, Positions,
       *  Balances, Activities, Settings). The V2 hero above is the
       *  only thing that changed; the tab strip + panels are identical. */}
      <AccountTabs
        account={account}
        bots={bots}
        theme={theme}
        isAggregate={isAggregateView}
        liveAccounts={ACCOUNTS.filter((a) => !a.paper)}
        rangeInfo={rangeInfo}
      />

      {/* Divider + footer disclaimer are hidden on xl: in split-pane
       *  mode the right column is a fixed-height container and the
       *  panel content above already fills it, so the 64px of `my:4`
       *  on the divider + disclaimer space creates exactly the
       *  "empty band before the fold" Sergey flagged. Mobile/narrow
       *  desktop keep them — the page itself scrolls there. */}
      <Divider sx={{ my: 4, display: { xl: "none" } }} />
      {account.paper && (
        <Typography
          variant="caption"
          color="text.secondary"
          textAlign="center"
          sx={{ display: { xs: "block", xl: "none" } }}
        >
          Paper trading account — no real money involved.
        </Typography>
      )}
      </Box>{/* ---- /Right column ---- */}

      <Fab
        color="primary"
        aria-label="Create"
        onClick={(e) => setCreateAnchor(e.currentTarget)}
        sx={{
          position: "fixed",
          right: 16,
          bottom: "calc(80px + env(safe-area-inset-bottom))",
          // Stay above the sticky tabs strip (zIndex ~5) and any
          // content underneath, but BELOW MUI drawer (1200) so an open
          // bot/position/order/buying-power sheet covers the FAB.
          // Sergey 2026-05-13.
          zIndex: 1150
        }}
      >
        <Add />
      </Fab>
    </Box>
  );
}

/* ---- Shared tab strip (mirrors V1's MobileAccountCard tabs) --------- */

type BotT = typeof BOTS[number];

/** Format an ISO yyyy-mm-dd into a compact "MMM D" label for the
 *  range section header ("Sep 2 – Apr 28"). Local-time parsing
 *  matches the chart's `isoToLocalDate` so the displayed day stays
 *  on the user's calendar regardless of timezone. */
function formatRangeIsoDate(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function AccountTabs({
  account, bots, theme, isAggregate, liveAccounts, rangeInfo
}: {
  account: BrokerAccount;
  bots: BotT[];
  theme: Theme;
  isAggregate: boolean;
  /** Hydrated list of LIVE accounts — forwarded to PositionsPanel +
   *  OrdersPanel so their aggregate fan-outs can target real accounts
   *  instead of the mock list. */
  liveAccounts: readonly BrokerAccount[];
  /** Active chart-range info — drives the MetricsPanel's range-scoped
   *  section. Empty `startIso`/`endIso` means the chart hasn't emitted
   *  a range yet. */
  rangeInfo: {
    key: string;
    label: string;
    customLabel: string | null;
    startIso: string;
    endIso: string;
  };
}) {
  const [tab, setTab] = useState(0);

  // Same TopAppBar-clearing offset the parent component computes, but
  // local to AccountTabs so the sticky-top math doesn't have to be
  // threaded through props. The `theme` prop already in scope is the
  // shared MUI theme, so this reads the same breakpoint as the
  // parent's `useMediaQuery(theme.breakpoints.up("md"))`.
  const isDesktopForTabs = useMediaQuery(theme.breakpoints.up("md"));
  const STICKY_TOP_OFFSET = isDesktopForTabs ? 72 : 0;

  // Wrapper ref — used to reach into the Tabs' internal `.MuiTabs-scroller`
  // element so we can drive the horizontal scroll ourselves on tab change.
  // Rationale for owning scroll manually: MUI's `variant="scrollable"` will
  // only auto-scroll a selected tab into view when it's about to clip off
  // the edge. Beginners whose selected tab is already visible never see
  // the strip move, so they have no cue that there are more tabs on either
  // side. We instead always center the clicked tab within the visible
  // scroller as much as possible — the strip slides under every pick, so
  // the user gets a visible cue that there's more content in both
  // directions. `scrollBy` naturally clamps at the scroll bounds, so the
  // first and last tabs just come to rest at the edge rather than
  // centering (which is the right behaviour — there's nothing past them).
  const tabsWrapperRef = useRef<HTMLDivElement>(null);

  // Center the tab at index `idx` within the horizontal scroller.
  //
  // History (Sergey 2026-05-13): the rAF-based version of this
  // function silently failed in production. CDP probe of the live
  // bundle showed:
  //   - clicks DID change React state (active tab swapped)
  //   - 2-rAF callbacks were scheduled but never reached the inner
  //     `querySelector(".MuiTabs-scroller")` line, meaning rAFs
  //     either didn't fire or fired with stale closures.
  //   - even bare `requestAnimationFrame(cb)` calls on the page
  //     timed out the CDP eval — the main thread was busy enough
  //     during tab transitions to stall rAF entirely.
  // Switching to `setTimeout` would inherit the same problem (also
  // main-thread-bound). The reliable path is to do the math now
  // (offsetLeft/offsetWidth — cached, doesn't force layout) and
  // assign `scrollLeft` directly. No animation queue, no rAF, no
  // dependency on the next idle frame.
  //
  // We still apply at +100/+250/+400 ms as belt-and-suspenders to
  // override any late MUI internal scroll-into-view that ran AFTER
  // ours. Direct scrollLeft writes are cheap, so the redundancy is
  // free.
  const centerTabInScroller = useCallback((idx: number) => {
    const apply = () => {
      const wrapper = tabsWrapperRef.current;
      if (!wrapper) return;
      const scroller = wrapper.querySelector<HTMLDivElement>(".MuiTabs-scroller");
      if (!scroller) return;
      const tabEls = scroller.querySelectorAll<HTMLElement>(".MuiTab-root");
      const target = tabEls[idx];
      if (!target) return;
      const targetCenter = target.offsetLeft + target.offsetWidth / 2;
      const viewportHalf = scroller.clientWidth / 2;
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      const desired = Math.max(0, Math.min(maxScroll, targetCenter - viewportHalf));
      if (Math.abs(scroller.scrollLeft - desired) > 0.5) {
        scroller.scrollLeft = desired;
      }
    };
    // Immediate apply — runs synchronously inside the click event,
    // so layout is current and we beat MUI's effect-phase scroll.
    apply();
    // Belt-and-suspenders: re-apply through the window where MUI's
    // internal scrollSelectedIntoView typically runs (~300ms).
    window.setTimeout(apply, 100);
    window.setTimeout(apply, 250);
    window.setTimeout(apply, 400);
  }, []);

  // Center on mount AND any tab change. This is the single source of
  // truth — handleTabChange just calls setTab. The setState triggers
  // a render, the [tab] effect runs after commit, and `apply()` fires
  // synchronously inside the effect (no scheduling needed).
  useEffect(() => {
    centerTabInScroller(tab);
    // Also re-center on window resize so narrow viewports keep the
    // active tab in frame when the user rotates or docks DevTools.
    const onResize = () => centerTabInScroller(tab);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [tab, centerTabInScroller]);

  const handleTabChange = (_: unknown, v: number) => {
    setTab(v);
    // Centering is handled by the [tab] useEffect — no duplicate
    // call here (Sergey 2026-05-13: duplicate calls caused
    // smooth/MUI race on repeat clicks).
  };

  return (
    <>
    {/* The sticky wrapper now contains ONLY the Tabs strip — the
     *  tab-panel `<Box>` below is a sibling. Reason: `position: sticky`
     *  with an element TALLER than the viewport doesn't stick visually
     *  (the element has no room to "stick" within its content height).
     *  The previous setup made the entire AccountTabs Box sticky, which
     *  measured ~1200px once panels with lists were rendered → sticky
     *  silently no-op'd. CDP probe 2026-05-13: chain[0] above the
     *  MuiTabs-root was a sticky Box of height 1212px — that's the
     *  smoking gun. Now sticky wraps ~48px of strip only. */}
    <Box
      ref={tabsWrapperRef}
      sx={{
        // Tab strip behaviour depends on layout:
        //   - md/lg single-column page scroll → sticky at top:72
        //     (clears the TopAppBar).
        //   - xl split-pane → static, but `flexShrink: 0` so it never
        //     gets squeezed by the flex:1 content area below it. The
        //     right column itself doesn't scroll on xl; the content
        //     Box that follows owns the scroll, so the tabs naturally
        //     sit at the top without needing sticky.
        position: { xs: "static", md: "sticky", xl: "static" },
        top: { md: STICKY_TOP_OFFSET },
        zIndex: { md: 5 },
        bgcolor: { md: "background.default" },
        flexShrink: { xl: 0 },
      }}
    >
      <Tabs
        value={tab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons={false}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          // No bottom margin on xl — the filter bar in each panel
          // sits flush against the tabs strip in the split-pane
          // layout (Sergey: "search/filter are part of the pinned
          // menu, remove the gap"). Mobile/narrow keeps a 16px
          // breath since the page itself scrolls.
          mb: { xs: 2, xl: 0 },
          minHeight: 40,
          "& .MuiTab-root": {
            minWidth: "auto",
            minHeight: 44,
            px: 1.5,
            fontWeight: 600,
            // body1 (1rem) — matches "Buying Power" below and the
            // range chips above so every clickable text element on
            // AccountsPageV2 reads at the same scale per Apr-2026
            // typography-consistency sweep.
            fontSize: "1rem",
            textTransform: "none"
          }
        }}
      >
        <Tab label="Metrics" />
        <Tab label="Orders" />
        <Tab label="Bots" />
        <Tab label="Positions" />
        <Tab label="Balances" />
        <Tab label="Activities" />
        <Tab label="Settings" />
      </Tabs>
    </Box>{/* /tabs wrapper */}

      {/* Panel content Box. On xl this hands its full height to
       *  whichever panel is active and lets the panel manage its own
       *  internal scroll. The panel renders a flex column where the
       *  filter bar is `flexShrink: 0` (stays put, no scrollbar
       *  beside it) and the list view itself is `flex: 1` +
       *  `overflowY: auto` — that way the scrollbar starts UNDER the
       *  filter, not at the top of the content area, and the list
       *  rows can extend to the bottom of the pane. */}
      <Box
        sx={{
          flex: { xl: 1 },
          minHeight: { xl: 0 },
          display: { xl: "flex" },
          flexDirection: { xl: "column" },
        }}
      >
        {tab === 0 && (
          // MetricsPanel has no filter bar, so wrap it in a scroll
          // Box here so the whole content area scrolls on xl. Filter-
          // owning panels (Orders/Bots/Positions/Activities) manage
          // their own internal scroll so the filter stays pinned.
          <Box
            sx={{
              flex: { xl: 1 },
              minHeight: { xl: 0 },
              overflowY: { xl: "auto" },
              // 8px breath under the tab strip underline so the gap
              // matches the inter-card grid gap (also 1 = 8px). Sergey
              // 2026-05-13: visual rhythm between rows should extend
              // to the row-to-tabs spacing too.
              pt: { xl: 1 },
              ...autoHideScrollbarSx,
            }}
          >
            <MetricsPanel
              account={account}
              bots={bots}
              isAggregate={isAggregate}
              liveAccounts={liveAccounts}
              theme={theme}
              rangeInfo={rangeInfo}
            />
          </Box>
        )}
        {tab === 1 && (
          <OrdersPanel
            account={account}
            bots={bots}
            isAggregate={isAggregate}
            liveAccounts={liveAccounts}
          />
        )}
        {tab === 2 && (
          <BotsPanel
            account={account}
            bots={bots}
            isAggregate={isAggregate}
            liveAccounts={liveAccounts}
          />
        )}
        {tab === 3 && (
          <PositionsPanel
            account={account}
            isAggregate={isAggregate}
            liveAccounts={liveAccounts}
          />
        )}
        {tab === 4 && (
          <Box
            sx={{
              flex: { xl: 1 },
              minHeight: { xl: 0 },
              overflowY: { xl: "auto" },
              // 8px breath under the tab strip underline so the gap
              // matches the inter-card grid gap (also 1 = 8px). Sergey
              // 2026-05-13: visual rhythm between rows should extend
              // to the row-to-tabs spacing too.
              pt: { xl: 1 },
              ...autoHideScrollbarSx,
            }}
          >
            <BalancesPanel
              account={account}
              bots={bots}
              isAggregate={isAggregate}
              liveAccounts={liveAccounts}
            />
          </Box>
        )}
        {tab === 5 && (
          <ActivitiesPanel
            account={account}
            isAggregate={isAggregate}
            liveAccounts={liveAccounts}
          />
        )}
        {tab === 6 && isAggregate && (
          <Box
            sx={{
              flex: { xl: 1 },
              minHeight: { xl: 0 },
              overflowY: { xl: "auto" },
              // 8px breath under the tab strip underline so the gap
              // matches the inter-card grid gap (also 1 = 8px). Sergey
              // 2026-05-13: visual rhythm between rows should extend
              // to the row-to-tabs spacing too.
              pt: { xl: 1 },
              ...autoHideScrollbarSx,
            }}
          >
            <PlaceholderPanel
              heading="Settings"
              body="Settings apply to a single account — pick one from the selector above to change its nickname, target APY, personal info, or disconnect the broker."
            />
          </Box>
        )}
        {tab === 6 && !isAggregate && (
          <Box
            sx={{
              flex: { xl: 1 },
              minHeight: { xl: 0 },
              overflowY: { xl: "auto" },
              // 8px breath under the tab strip underline so the gap
              // matches the inter-card grid gap (also 1 = 8px). Sergey
              // 2026-05-13: visual rhythm between rows should extend
              // to the row-to-tabs spacing too.
              pt: { xl: 1 },
              ...autoHideScrollbarSx,
            }}
          >
            <SettingsView account={account} />
          </Box>
        )}
      </Box>
    </>
  );
}

/**
 * Orange accent matching the SPY dot/line in PortfolioVsSpyChart
 * (`SERIES_COLOURS.spy = "#f97316"`). Kept inline here — MUI's palette
 * doesn't have a bucket for benchmark colours and the chart uses a
 * raw hex too. If a third surface ever needs this, promote to theme.
 */
const SPY_ACCENT = "#f97316";

/**
 * Shared scrollbar styling for the per-panel list scroll areas on
 * xl (split-pane layout). Goals:
 *   - 8px wide gutter, slightly rounded thumb.
 *   - Transparent thumb at rest, fades to a soft grey on hover or
 *     while the user is actively scrolling (`:focus-within` is the
 *     closest cross-browser proxy for "scrolling-now" without
 *     wiring a JS scroll listener).
 *   - Right-padding 8px on the host so list rows don't touch the
 *     scrollbar gutter (Sergey: needs 1–3px breathing room).
 *
 * Apply by spreading: `sx={{ ...autoHideScrollbarSx }}`.
 */
const autoHideScrollbarSx = {
  pr: { xl: 1 },
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    bgcolor: "transparent",
    borderRadius: 4,
    border: "2px solid transparent",
    backgroundClip: "padding-box",
    transition: "background-color 200ms ease",
  },
  "&:hover::-webkit-scrollbar-thumb, &:focus-within::-webkit-scrollbar-thumb":
    {
      bgcolor: "action.disabled",
    },
  // Firefox equivalent — slim auto-styled scrollbar that fades to
  // background when idle and picks up a soft grey while interacting.
  scrollbarWidth: "thin" as const,
  scrollbarColor: "transparent transparent",
  "&:hover, &:focus-within": {
    scrollbarColor: "rgba(160,170,190,0.5) transparent",
  },
} as const;

/**
 * Metric card used throughout the Metrics tab.
 *
 * Three optional secondary rows, rendered in this order when present:
 *   1. `delta.pct`            — tinted %-change line (green/red/neutral).
 *                               Original pre-V2 behaviour; uses formatPct
 *                               which takes a *fraction* (0.05 → "+5.00%").
 *   2. `sub`                  — plain muted text (e.g. "+$234 effective",
 *                               "TWR: +8.4%", "Excl. bots"). Rendered as-is.
 *   3. `spyCompare`           — "SPY +X.XX%" benchmark line, coloured
 *                               SPY orange so it reads as the benchmark
 *                               legend colour from the chart.
 *                               • number → formatted as percent-scale
 *                                 (12.5 → "+12.50%"). Use this for
 *                                 returns/APY/drawdown.
 *                               • string → rendered verbatim after the
 *                                 "SPY " prefix. Use this for non-%
 *                                 benchmarks like Sharpe, where the
 *                                 caller formats the raw ratio itself.
 *
 * `onClick` — when present, the whole card becomes tappable with a
 * subtle hover elevation. Used for the Cash card toggle (excl-bots ↔
 * total); could be reused for any card with cycle-through behaviour.
 */
function MetricBox({
  label, value, delta, sub, subs, spyCompare, onClick, theme, term, valueColor,
  onHide
}: {
  label: string;
  value: string;
  delta?: { pct?: number; neutral?: boolean };
  sub?: string;
  /**
   * Multi-line sub-details. When present, each string renders on its
   * own line (instead of the joined middot-separated single-line `sub`).
   * Used for cards like Weighted APY where "Target" and "TWR-APY" are
   * two independent data points — stacking them makes each one
   * scannable on mobile instead of hiding behind a "·" separator.
   */
  subs?: string[];
  spyCompare?: number | string;
  onClick?: () => void;
  theme: Theme;
  term?: Term;
  /**
   * Override for the big-value text colour. Lets callers signal
   * warning states (e.g. APY < Target → yellow, negative $ → red)
   * without adding a bool to this component for every semantic state.
   */
  valueColor?: string;
  /**
   * When set, a desktop-only (×) appears on hover to hide the card.
   * Mobile does not render it — hiding on mobile happens via the
   * Metrics settings dialog (mouse-hover doesn't apply to touch).
   */
  onHide?: () => void;
}) {
  const bg = theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.04)
    : alpha(theme.palette.common.black, 0.03);

  const labelNode = (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography
        variant="caption"
        component="span"
        sx={{
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontSize: "0.6875rem",
          fontWeight: 600
        }}
      >
        {label}
      </Typography>
      {term && <InfoOutlined sx={{ fontSize: 12, color: "text.disabled" }} />}
    </Stack>
  );

  return (
    <Box
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        bgcolor: bg,
        borderRadius: 2,
        // Padding + min-height bumped 2026-05-13 to bring the cards
        // closer to Material Design 3 "container" sizing on desktop
        // (cramped tiles read as labels rather than data). Mobile gets
        // a smaller bump since vertical real estate is tighter.
        p: { xs: 1.5, md: 2 },
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        minHeight: { xs: 84, md: 104 },
        position: "relative",
        cursor: onClick ? "pointer" : term ? "help" : "default",
        userSelect: onClick ? "none" : "auto",
        transition: "background-color 120ms ease",
        // Desktop-only: the close-X fades in on hover. `@media (hover: hover)`
        // keeps it off touch devices where hover is a no-op. The button
        // itself is visibility:hidden at rest so it doesn't steal the
        // pointer from the card's own onClick handler. Restored 2026-05-13
        // per Sergey after a brief removal.
        ...(onHide
          ? {
              "@media (hover: hover)": {
                "&:hover .MetricBox-hideBtn": {
                  opacity: 1,
                  visibility: "visible" as const
                }
              }
            }
          : {}),
        "&:hover": onClick
          ? {
              bgcolor: theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.07)
                : alpha(theme.palette.common.black, 0.05)
            }
          : undefined
      }}
    >
      {onHide && (
        <IconButton
          className="MetricBox-hideBtn"
          size="small"
          aria-label={`Hide ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            onHide();
          }}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            // Slightly larger hit target now that the cards themselves
            // grew (MD3 sizing pass) — 14px icon read too small against
            // the bigger container. 18px hits a comfortable size with
            // the same visibility:hidden-on-rest behaviour.
            p: 0.5,
            opacity: 0,
            visibility: "hidden",
            transition: "opacity 120ms ease",
            color: "text.secondary",
            "&:hover": { color: "error.main" }
          }}
        >
          <CloseRounded sx={{ fontSize: 18 }} />
        </IconButton>
      )}
      {term
        ? <MetricTip term={term}><span>{labelNode}</span></MetricTip>
        : labelNode}
      <Typography
        sx={(t) => ({
          fontWeight: 700,
          fontSize: "1.0625rem",
          fontFeatureSettings: '"tnum" 1',
          // Auto-colour values that lead with a directional triangle:
          // ▲ → green (primary), ▼ → red (error). Sergey 2026-04-30
          // — same convention as the delta indicator and the TOTAL%
          // chip on the Positions tab. Explicit `valueColor` overrides
          // the auto rule.
          ...(valueColor
            ? { color: valueColor }
            : value.startsWith("▲")
              ? { color: t.palette.primary.main }
              : value.startsWith("▼")
                ? { color: t.palette.error.main }
                : {})
        })}
      >
        {value}
      </Typography>
      {delta?.pct !== undefined && (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: delta.neutral
              ? "text.secondary"
              : delta.pct >= 0 ? "primary.main" : "error.main",
            fontFeatureSettings: '"tnum" 1'
          }}
        >
          {/* Sergey 2026-04-30: replaced ± prefix with directional
           *  triangles. Color (green / red) already conveys sign;
           *  the triangle reinforces direction at a glance and matches
           *  the convention used in BotsSimpleView. Neutral keeps the
           *  raw signed text since there's no direction to indicate. */}
          {delta.neutral
            ? formatPct(delta.pct)
            : `${delta.pct >= 0 ? "▲" : "▼"} ${Math.abs(delta.pct * 100).toFixed(2)}%`}
        </Typography>
      )}
      {sub && !subs && (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontFeatureSettings: '"tnum" 1',
            lineHeight: 1.3
          }}
        >
          {sub}
        </Typography>
      )}
      {subs && subs.length > 0 && subs.map((line, i) => (
        <Typography
          key={i}
          variant="caption"
          sx={{
            color: "text.secondary",
            fontFeatureSettings: '"tnum" 1',
            lineHeight: 1.3
          }}
        >
          {line}
        </Typography>
      ))}
      {spyCompare !== undefined && (
        <Typography
          variant="caption"
          sx={{
            // SPY orange — matches the SPY dot/line in the
            // PortfolioVsSpyChart legend so the benchmark number is
            // visually tied to the series it refers to.
            color: SPY_ACCENT,
            fontFeatureSettings: '"tnum" 1',
            lineHeight: 1.3,
            fontWeight: 600
          }}
        >
          SPY {typeof spyCompare === "number" ? formatWeeklyPct(spyCompare) : spyCompare}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Format a max-recovery-time span for the MRec card.
 *
 * Days below ~60 read as days ("42d"); longer spans read as months so
 * the "prefer under 6 months" rule of thumb is easy to apply at a
 * glance ("7.3mo"). An unresolved stretch gets a "+" — the clock is
 * still running, so the true figure is at least this.
 */
function formatRecoveryDays(days: number, ongoing: boolean): string {
  if (!Number.isFinite(days) || days <= 0) return "None";
  const suffix = ongoing ? "+" : "";
  if (days < 60) return `${Math.round(days)}d${suffix}`;
  const months = days / 30.44;
  return `${months.toFixed(1)}mo${suffix}`;
}

/**
 * Metrics tab content for the active account.
 *
 * Card order follows the WHB weekly-report summary grid:
 *   Weekly TWR → Day change → Weighted APY → YTD → All-time →
 *   Cash (toggleable) → Net Deposits → Stocks → Holdings → Bots →
 *   Max drawdown → Sharpe
 *
 * "Cash" here is a dual-mode card — click to flip between the two
 * buying-power variants the rest of the app shows. Kept as local state
 * (no URL param) because the selection is cosmetic and shouldn't show
 * up in bookmarks. Persisted across re-renders of the panel but reset
 * when the user switches accounts (since MetricsPanel is keyed by
 * `account.id` at the tab level — see AccountTabs).
 *
 * SPY compare lines appear on the four cards where they're meaningful:
 * Weekly TWR, Weighted APY, YTD, and All-time. Stocks / Cash / Holdings
 * / Bots / Max drawdown / Sharpe don't get one — either they're not
 * returns (holdings counts, cash balances) or the SPY equivalent is
 * already baked into the portfolio-vs-SPY chart elsewhere on the page.
 */
function MetricsPanel({
  account,
  bots,
  isAggregate,
  liveAccounts,
  theme,
  rangeInfo
}: {
  account: BrokerAccount;
  bots: BotT[];
  isAggregate: boolean;
  liveAccounts: readonly BrokerAccount[];
  theme: Theme;
  rangeInfo: {
    key: string;
    label: string;
    customLabel: string | null;
    startIso: string;
    endIso: string;
  };
}) {
  // Pull positions for the Stocks card's cost-basis sub-line. Hooks
  // are cache-first so this duplicates no network — positions might
  // already be hydrated from a prior visit to the Positions tab.
  // Single-account: just the current account; aggregate: union across
  // every live account. Demo / non-real accounts skip both hooks
  // (returns empty positions, sub-line falls through to "—").
  const _kind = useAuthStore((s) => s.kind);
  const _isRealSingle =
    _kind === "real" && !isAggregate && account.source === "real";
  const { positions: _realSinglePositions } = useRealAccountPositions(
    _isRealSingle ? account.id : null
  );
  const { positions: _realLivePositions } = useRealLiveAccountPositions(
    isAggregate ? liveAccounts : []
  );
  const totalCostBasis = useMemo(() => {
    const list = isAggregate ? _realLivePositions : _realSinglePositions;
    if (!list || list.length === 0) return 0;
    return list.reduce((s, p) => s + (p.costBasis || 0), 0);
  }, [isAggregate, _realSinglePositions, _realLivePositions]);
  const [cashMode, setCashMode] = useState<"ex-bots" | "total">("ex-bots");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const order = useMetricsSettingsStore((s) => s.order);
  const hidden = useMetricsSettingsStore((s) => s.hidden);
  const hide = useMetricsSettingsStore((s) => s.hide);
  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);
  const effectiveOrder = useMemo<MetricCardId[]>(() => {
    const seen = new Set(order);
    const tail = DEFAULT_ORDER.filter((id) => !seen.has(id));
    return [...order, ...tail];
  }, [order]);
  const visibleOrder = useMemo(
    () => effectiveOrder.filter((id) => !hiddenSet.has(id)),
    [effectiveOrder, hiddenSet]
  );

  const m = deriveAccountMetrics(account);

  // Signed-dollar helpers — one place to decide sign indicator +
  // format. Sergey 2026-04-30: ± replaced with directional triangles
  // (▲ ▼) to match the delta indicators inside MetricBox. Sub-lines
  // are rendered in muted text without colour, so the triangle alone
  // carries the direction; zero stays unmarked.
  const signedUsd = (v: number): string => {
    const abs = formatUSD(Math.abs(v), { maximumFractionDigits: 0 });
    if (v > 0) return `▲ ${abs}`;
    if (v < 0) return `▼ ${abs}`;
    return abs;
  };

  // All-time return — cashflow-neutral $ delta from inception. Uses the
  // chart's exact `curEquity − startEquity − postOpenCashflows` formula
  // via `windowDollarReturn` in `chart-math.ts`. Not `account.allTimeChange`
  // (unrealised P&L on currently-held stocks — can flip sign even on
  // a winning account when capital is withdrawn).
  const allTimeSigned = signedUsd(m.allTimeTwrDollar);

  // Weekly dollar effective line — e.g. "+$234 effective".
  const weeklyDollarSub = `${m.weeklyTwrDollar >= 0 ? "+" : ""}${formatUSD(m.weeklyTwrDollar, { maximumFractionDigits: 0 })} effective`;

  // Weighted APY sub-lines — broken onto separate rows per Apr-2026
  // Sergey feedback: "make sure each definition-value on separate line".
  // Previously joined with " · "; that packed two independent data
  // points into one visual token and forced the user to parse a
  // middot separator. Stacked rows scan faster.
  // Target is shown with fixed 2-decimal precision to match GIPS / industry
  // performance-display convention ("20.00%", "12.50%") and to suppress the
  // floating-point artifacts that surface when targetApyPct is the result
  // of arithmetic (e.g. 0.20 * 100 → 20.000000000000004). TWR-APY stays at
  // 1 decimal to match the hero value.
  const weightedApySubs = [
    `Target: ${account.targetApyPct.toFixed(2)}%`,
    `TWR-APY: ${account.risk.apyPct.toFixed(1)}%`
  ];
  // Value colour for the Weighted APY card: yellow when the portfolio
  // is underperforming its stated target, default when it meets or
  // beats target. Warning colour matches the MUI theme palette so it
  // adapts to light/dark automatically.
  const weightedApyBelowTarget =
    m.weightedApyPct < account.targetApyPct;
  const weightedApyValueColor = weightedApyBelowTarget
    ? "warning.main"
    : undefined;

  // Today vs Previous day — when the US equity market is closed (after
  // hours, weekend, or overnight) the last sample in the curve is not
  // "today" at all but the most recent trading day's close. Relabel so
  // the UI doesn't lie about what the number represents.
  const marketState = getMarketState();
  const isTodayCardPrevious = marketState === "closed";
  const todayCardLabel = isTodayCardPrevious ? "Previous day" : "Today";
  // Previous-day return — single-day ratio return on the cumulative
  // TWR curve. We compute in DECIMAL space using the shared chart-math
  // helper so the card uses the exact formula the graph emits.
  // DECIMAL convention (0.0013 = +0.13 %) is what `formatPct` expects,
  // and what the chart's hero `pctChange` emits.
  //
  // Safety clamp: if the TWR curve has a reconstruction-induced jump
  // on the last sample (correction anchor snapping to Alpaca's live
  // cash balance), the ratio can explode. ±20 % is well past any
  // plausible single-day equity move, so the clamp is a cap on the
  // card's display only — the underlying TWR series is unchanged.
  const n = account.twrCurve.length;
  const rawPortfolioDayDec =
    n < 2 ? 0 : windowReturnDecimal(account.twrCurve, n - 2, n - 1);
  const portfolioDayDec = Math.max(-0.2, Math.min(0.2, rawPortfolioDayDec));
  const spyDayDec = (() => {
    const s = account.spyCurve;
    if (s.length < 2) return 0;
    return windowReturnDecimal(s, s.length - 2, s.length - 1);
  })();
  // Percent-scale companions for `formatWeeklyPct` / SPY chip which
  // expect already-percent input.
  const spyDayPct = +(spyDayDec * 100).toFixed(3);
  // Previous-day dollar return — chart's cashflow-neutral formula over
  // the single last-day segment. The Previous Day card now leads with
  // this (white/red $), with the % beneath as secondary info.
  const previousDayDollar =
    n < 2
      ? 0
      : windowDollarReturn(
          account.equityCurve,
          account.curveDates,
          account.cashflows,
          n - 2,
          n - 1
        );
  const previousDaySigned = signedUsd(previousDayDollar);
  const previousDayValueColor =
    previousDayDollar < 0 ? "error.main" : undefined;
  // Bot count — prefer `account.botIds.length` since that's the union
  // on the aggregate. Falls back to the loaded `bots` prop when the
  // botIds array is missing.
  const botCount = account.botIds?.length ?? bots.length;

  // Cash card:
  //   • "cash" mode   → actual account cash balance (matches Alpaca
  //     "Cash" figure on the broker side). This is what most people
  //     mean by "how much cash do I have".
  //   • "buying power" mode → margin-inflated buying power the
  //     brokerage would let you deploy (4x for day-trading on cash,
  //     2x for reg T). The old card labelled this "Cash" which was
  //     misleading; Sergey Apr-2026: "Cash in metrics is wrong".
  //     Split out explicitly and tap-to-toggle.
  const cashCardValue = cashMode === "ex-bots"
    ? account.cashValue
    : account.buyingPower;
  const cashCardLabel = cashMode === "ex-bots" ? "Cash" : "Buying Power";
  const cashSub = cashMode === "ex-bots"
    ? "Account cash · tap for buying power"
    : "Margin total · tap for cash";
  const cashTerm = cashMode === "ex-bots" ? TERMS.buyingPowerExBots : TERMS.buyingPower;

  const ytdYear = new Date(account.curveDates[account.curveDates.length - 1]!).getFullYear();

  // Lifetime MRec — longest underwater stretch over the whole TWR
  // curve, alongside SPY's for comparison. Memoised because it walks
  // the full curve on every render otherwise.
  const lifetimeRecovery = useMemo(
    () => maxRecoveryTime(account.twrCurve, account.curveDates),
    [account.twrCurve, account.curveDates]
  );
  const lifetimeRecoverySpy = useMemo(
    () => maxRecoveryTime(account.spyCurve, account.curveDates),
    [account.spyCurve, account.curveDates]
  );

  // Net Deposits inflow/outflow sub-lines, prebuilt once so the catalog
  // below stays readable.
  const netDepositsSubs: string[] = [];
  if (m.depositsCount > 0) {
    netDepositsSubs.push(
      `${m.depositsCount} deposit${m.depositsCount === 1 ? "" : "s"}`
    );
  }
  if (m.withdrawalsCount > 0) {
    netDepositsSubs.push(
      `${m.withdrawalsCount} withdrawal${m.withdrawalsCount === 1 ? "" : "s"}`
    );
  }

  // Catalog: one entry per card id, returning the full JSX for that
  // card (minus the store-driven `onHide` wiring, which we attach in
  // the render loop). Keeps the render pass simple — just iterate the
  // store's ordered list and look each id up here.
  const catalog: Record<MetricCardId, (onHide: () => void) => React.ReactNode> = {
    weeklyTwr: (onHide) => (
      <MetricBox key="weeklyTwr" theme={theme} label="Weekly TWR"
        value={formatWeeklyPct(m.weeklyTwrPct)}
        sub={weeklyDollarSub}
        spyCompare={m.weeklyTwrSpyPct}
        term={TERMS.weeklyTwr}
        onHide={onHide} />
    ),
    previousDay: (onHide) => (
      // Previous Day / Today — primary value is the signed dollar
      // change (white for gains, red for losses). The % moves to the
      // delta chip underneath (green/red). Previously the card led
      // with the % and put $ behind; Sergey Apr-2026 asked for the
      // reverse so the dollar impact of the day is the scannable one.
      <MetricBox key="previousDay" theme={theme} label={todayCardLabel}
        value={previousDaySigned}
        valueColor={previousDayValueColor}
        delta={{ pct: portfolioDayDec }}
        spyCompare={spyDayPct}
        term={TERMS.dayChange}
        onHide={onHide} />
    ),
    weightedApy: (onHide) => (
      <MetricBox key="weightedApy" theme={theme} label="Weighted APY"
        value={formatWeeklyPct(m.weightedApyPct, 1)}
        subs={weightedApySubs}
        spyCompare={m.weightedApySpyPct}
        valueColor={weightedApyValueColor}
        term={TERMS.weightedApy}
        onHide={onHide} />
    ),
    ytd: (onHide) => (
      <MetricBox key="ytd" theme={theme} label={`YTD ${ytdYear}`}
        value={formatWeeklyPct(m.ytdPct)}
        sub={signedUsd(m.ytdDollar) + " effective"}
        spyCompare={m.ytdSpyPct}
        term={TERMS.ytdTwr}
        onHide={onHide} />
    ),
    pastMonth: (onHide) => (
      // Past Month — trailing 30 calendar days, matching the chart's
      // 1M range. $ first (cashflow-neutral), % + SPY beneath.
      <MetricBox key="pastMonth" theme={theme} label="Past Month"
        value={signedUsd(m.pastMonthDollar)}
        valueColor={m.pastMonthDollar < 0 ? "error.main" : undefined}
        delta={{ pct: m.pastMonthPct / 100 }}
        spyCompare={m.pastMonthSpyPct}
        term={TERMS.pastMonth}
        onHide={onHide} />
    ),
    allTimeReturn: (onHide) => (
      <MetricBox key="allTimeReturn" theme={theme} label="All-time return"
        value={allTimeSigned}
        valueColor={m.allTimeTwrDollar < 0 ? "error.main" : undefined}
        delta={{ pct: m.allTimeTwrPct / 100 }}
        spyCompare={m.allTimeSpyPct}
        term={TERMS.dollarReturn}
        onHide={onHide} />
    ),
    cash: (onHide) => (
      <MetricBox key="cash" theme={theme} label={cashCardLabel}
        value={formatUSD(cashCardValue, { maximumFractionDigits: 0 })}
        sub={cashSub}
        onClick={() => setCashMode((v) => v === "ex-bots" ? "total" : "ex-bots")}
        term={cashTerm}
        onHide={onHide} />
    ),
    netDeposits: (onHide) => (
      <MetricBox key="netDeposits" theme={theme} label="Net Deposits"
        value={formatUSD(m.netDeposits, { maximumFractionDigits: 0 })}
        subs={netDepositsSubs}
        term={TERMS.netDeposits}
        onHide={onHide} />
    ),
    stocks: (onHide) => {
      // Unrealized P&L % vs cost basis: (market value − cost basis)
      // / cost basis. Same colour treatment as other delta indicators
      // (green ≥ 0, red < 0). Hidden when costBasis isn't loaded yet.
      const unrealizedPct =
        totalCostBasis > 0
          ? (account.stocksValue - totalCostBasis) / totalCostBasis
          : undefined;
      return (
        <MetricBox key="stocks" theme={theme} label="Stocks"
          value={formatUSD(account.stocksValue, { maximumFractionDigits: 0 })}
          delta={
            unrealizedPct !== undefined ? { pct: unrealizedPct } : undefined
          }
          sub={
            totalCostBasis > 0
              ? `Cost basis ${formatUSD(totalCostBasis, { maximumFractionDigits: 0 })}`
              : undefined
          }
          term={TERMS.stocks}
          onHide={onHide} />
      );
    },
    holdings: (onHide) => (
      <MetricBox key="holdings" theme={theme} label="Holdings"
        value={String(account.stocksCount)}
        term={TERMS.holdings}
        onHide={onHide} />
    ),
    bots: (onHide) => (
      <MetricBox key="bots" theme={theme} label="Bots"
        value={String(botCount)}
        term={TERMS.botsCount}
        onHide={onHide} />
    ),
    maxDrawdown: (onHide) => (
      <MetricBox key="maxDrawdown" theme={theme} label="Max drawdown"
        value={`${account.risk.maxDDPct >= 0 ? "+" : ""}${account.risk.maxDDPct.toFixed(2)}%`}
        spyCompare={account.risk.maxDDSpyPct}
        term={TERMS.maxDrawdown}
        onHide={onHide} />
    ),
    sharpe: (onHide) => (
      // Sharpe is a raw ratio, not a percentage — pass the formatted
      // string so the card shows "SPY 0.42" instead of "SPY +0.42%".
      <MetricBox key="sharpe" theme={theme} label="Sharpe ratio"
        value={account.risk.sharpe.toFixed(2)}
        spyCompare={account.risk.sharpeSpy.toFixed(2)}
        term={TERMS.sharpe}
        onHide={onHide} />
    ),
    maxRecovery: (onHide) => (
      // Lifetime MRec — same metric as the range card, computed over
      // the account's whole TWR curve.
      <MetricBox key="maxRecovery" theme={theme} label="MRec"
        value={formatRecoveryDays(
          lifetimeRecovery.days,
          lifetimeRecovery.ongoing
        )}
        sub={
          lifetimeRecovery.ongoing && lifetimeRecovery.days > 0
            ? "still underwater"
            : undefined
        }
        spyCompare={formatRecoveryDays(lifetimeRecoverySpy.days, false)}
        term={TERMS.maxRecoveryTime}
        onHide={onHide} />
    )
  };

  const bg = theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.04)
    : alpha(theme.palette.common.black, 0.03);

  // ---- Range-scoped metrics (Sergey 2026-04-30) -------------------------
  //
  // Renders an extra "Range" section above the per-card grid when:
  //   • the user has the toggle ON (default true; toggle in
  //     MetricsSettingsDialog), AND
  //   • the chart's active range is NOT the lifetime ("ALL") view —
  //     ALL would just duplicate the All-time / Weighted-APY / Max-DD /
  //     Sharpe cards already in the grid.
  //
  // Cards: Range Return / Weighted APY (hidden if window <90 days) /
  // Max DD / Sharpe / YTD-as-of-end. SPY comparison rebased to the
  // window's left edge — same convention the chart uses.
  const showRangeMetrics = useMetricsSettingsStore(
    (s) => s.showRangeMetrics
  );
  const rangeMetrics = useMemo(
    () =>
      showRangeMetrics &&
      rangeInfo.key !== "ALL" &&
      rangeInfo.startIso &&
      rangeInfo.endIso
        ? deriveRangeScopedMetrics(
            account,
            rangeInfo.startIso,
            rangeInfo.endIso
          )
        : null,
    [showRangeMetrics, rangeInfo.key, rangeInfo.startIso, rangeInfo.endIso, account]
  );
  const rangeHeaderLabel = rangeMetrics
    ? rangeInfo.customLabel ?? rangeInfo.label
    : "";
  const rangeHeaderDates = rangeMetrics
    ? `${formatRangeIsoDate(rangeMetrics.startIso)} – ${formatRangeIsoDate(rangeMetrics.endIso)}`
    : "";

  return (
    <>
      {rangeMetrics && (
        <Box sx={{ mb: 2 }}>
          {/* Section header — distinguishes the Range cards from the
           *  lifetime cards beneath. Range label ("1M" / "Sep 2 –
           *  Apr 28") on the left, concrete date span on the right
           *  for unambiguous "what window am I looking at" feedback. */}
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            sx={{ mb: 0.75 }}
          >
            <Typography
              variant="overline"
              sx={{ color: "text.secondary", letterSpacing: 0.5, fontWeight: 700 }}
            >
              Range · {rangeHeaderLabel}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {rangeHeaderDates}
            </Typography>
          </Stack>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 1
            }}
          >
            <MetricBox
              theme={theme}
              label="Range return"
              value={`${rangeMetrics.rangeReturnPct >= 0 ? "+" : ""}${rangeMetrics.rangeReturnPct.toFixed(2)}%`}
              sub={signedUsd(rangeMetrics.rangeReturnDollar) + " effective"}
              spyCompare={rangeMetrics.rangeReturnSpyPct}
              term={TERMS.rangeReturn}
            />
            {rangeMetrics.rangeApyPct !== null && (
              <MetricBox
                theme={theme}
                label="Weighted APY (range)"
                value={formatWeeklyPct(rangeMetrics.rangeApyPct, 1)}
                spyCompare={rangeMetrics.rangeApySpyPct ?? undefined}
                term={TERMS.weightedApy}
              />
            )}
            <MetricBox
              theme={theme}
              label="Max drawdown (range)"
              value={`${rangeMetrics.rangeMaxDDPct >= 0 ? "+" : ""}${rangeMetrics.rangeMaxDDPct.toFixed(2)}%`}
              spyCompare={rangeMetrics.rangeMaxDDSpyPct}
              term={TERMS.maxDrawdown}
            />
            <MetricBox
              theme={theme}
              label="MRec (range)"
              value={formatRecoveryDays(
                rangeMetrics.rangeMaxRecoveryDays,
                rangeMetrics.rangeMaxRecoveryOngoing
              )}
              sub={
                rangeMetrics.rangeMaxRecoveryOngoing &&
                rangeMetrics.rangeMaxRecoveryDays > 0
                  ? "still underwater"
                  : undefined
              }
              spyCompare={formatRecoveryDays(
                rangeMetrics.rangeMaxRecoverySpyDays,
                false
              )}
              term={TERMS.maxRecoveryTime}
            />
            {rangeMetrics.rangeSharpe !== null && (
              <MetricBox
                theme={theme}
                label="Sharpe (range)"
                value={rangeMetrics.rangeSharpe.toFixed(2)}
                spyCompare={
                  rangeMetrics.rangeSharpeSpy !== null
                    ? rangeMetrics.rangeSharpeSpy.toFixed(2)
                    : undefined
                }
                term={TERMS.sharpe}
              />
            )}
            <MetricBox
              theme={theme}
              label={`YTD ${rangeMetrics.rangeYtdYear}`}
              value={formatWeeklyPct(rangeMetrics.rangeYtdPct)}
              sub={signedUsd(rangeMetrics.rangeYtdDollar) + " effective"}
              spyCompare={rangeMetrics.rangeYtdSpyPct}
              term={TERMS.ytdTwr}
            />
          </Box>
          <Divider sx={{ mt: 2 }} />
        </Box>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 1
        }}
      >
        {visibleOrder.map((id) => catalog[id](() => hide(id)))}
        {/* "+" tile — opens the management dialog. Visually matches the
         *  other cards' bg + rounding so the grid stays rhythmic, but
         *  has no real data. Aria-labelled so screen readers announce
         *  the purpose. */}
        <Box
          role="button"
          aria-label="Manage metrics"
          onClick={() => setSettingsOpen(true)}
          sx={{
            bgcolor: bg,
            borderRadius: 2,
            p: 1.5,
            minHeight: 72,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            cursor: "pointer",
            color: "text.secondary",
            transition: "background-color 120ms ease, color 120ms ease",
            "&:hover": {
              color: "primary.main",
              bgcolor: theme.palette.mode === "dark"
                ? alpha(theme.palette.common.white, 0.07)
                : alpha(theme.palette.common.black, 0.05)
            }
          }}
        >
          <AddCircleOutlineRounded sx={{ fontSize: 28 }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Add / manage
          </Typography>
        </Box>
      </Box>
      <MetricsSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
}

/**
 * Bots panel — owns filter state and dispatches between Simple
 * (flat dense rows) and Advanced (11-col report table) views.
 *
 * Why state lives here (not on the two views):
 *  • The filter bar and both views need to read the same filter state,
 *    and the sorted list needs to stay stable when the user toggles
 *    Simple ↔ Advanced — lifting state up keeps both in sync without
 *    re-plumbing every time.
 *  • Matches the pattern established by `OrdersPanel` above, which
 *    keeps its own OrdersFilterState.
 */
function BotsPanel({
  account,
  bots,
  isAggregate,
  liveAccounts
}: {
  account: BrokerAccount;
  bots: BotT[];
  isAggregate: boolean;
  liveAccounts: readonly BrokerAccount[];
}) {
  const [filter, setFilter] = useState<BotsFilterState>(DEFAULT_BOTS_FILTER);
  // Drawer-host state — taps on a bot row open the detail drawer
  // (Sergey 2026-04-26 strip-down). The drawer's "View bot page" CTA
  // is what actually routes to /bots/:id; the row tap itself only
  // surfaces the metrics that the simple row dropped.
  const [detailBot, setDetailBot] = useState<BotT | null>(null);
  const kind = useAuthStore((s) => s.kind);

  // Resolve bots for this view. Two pieces have to come right (Sergey
  // 2026-04-26):
  //
  //   1. Aggregate ("All Live Accounts"): the parent's `bots` prop
  //      filters MOCK BOTS by `account.botIds`. For the aggregate
  //      sentinel that ID list is the union of every live account's
  //      bot ids — but those are real-session UUIDs that don't match
  //      any mock-data id, so the filter returns []. Result: "no bots
  //      attached" on aggregate, even though `/bots` has plenty.
  //
  //   2. Single account: the parent uses useRealBotsForAccount
  //      which returns null when account.source !== "real". For
  //      ordinary real-session single accounts that's fine — but if
  //      that hook hasn't hydrated yet, the parent falls through to
  //      the same MOCK-filter path and surfaces the wrong bots.
  //
  // Fix: pull the unscoped `/bots` list directly here and filter by
  // account.botIds. For aggregate, that's the live-account union.
  // For single, that's the per-account list. Same code path, no
  // conditional null-handling.
  const { bots: realBotsAll } = useRealBots();

  // /bots returns portfolioValue=0 for many active bots and carries
  // no per-bot historical state, so we hit `/api/bots/{id}` for each
  // bot in the scope. That endpoint exposes `portfolio.equity` (real
  // current value) and `states[]` (daily cash/stocks/margin) — enough
  // to drive both the value override and the weekly delta. Recon
  // 2026-04-26: `/broker-accounts/{id}.states[]` only contains
  // account-level rows (botId === null), so it can't power per-bot
  // weekly deltas (probed live).
  const overlayBotIds = useMemo<string[]>(() => {
    if (kind !== "real") return [];
    const allowed = new Set(account.botIds);
    const all = realBotsAll ?? [];
    // Sergey 2026-04-30: dropped the "if allowed empty, return all"
    // fallback — when an account legitimately has no bots (e.g. user
    // deleted all of them), `realBotsAll` may still contain bots from
    // the user's *paper* accounts, and falling through surfaced those
    // paper bots in the live view. Truth wins: no botIds → no bots.
    return all.filter((b) => allowed.has(b.id)).map((b) => b.id);
  }, [kind, realBotsAll, account.botIds]);

  // ---- Archived bots (Sergey 2026-04-30) -------------------------------
  //
  // Archived bots = bots once visible in /bots that have since been
  // deleted. Sourced entirely from local cache: a tiny registry in
  // IndexedDB tracks every bot id we've ever seen, paired with the
  // last-cached `/bots/{id}` detail blob for state. 6-month retention.
  // Per-account list mirrors the active scope: aggregate "All Live"
  // takes the union across every live account; single-account view
  // takes only that account.
  const archivedAccountIds = useMemo<string[]>(() => {
    if (kind !== "real") return [];
    if (isAggregate) {
      return liveAccounts
        .filter((a) => a.source === "real")
        .map((a) => a.id);
    }
    return account.source === "real" ? [account.id] : [];
  }, [kind, isAggregate, liveAccounts, account]);
  const {
    bots: archivedBots,
    curves: archivedCurves,
    overrides: archivedOverrides,
    setOverride: setArchivedOverride
  } = useArchivedBots(archivedAccountIds);
  const [archivedExpanded, setArchivedExpanded] = useState(false);
  // Apply the same filter/sort the user picked for active bots, so
  // search + sort behave consistently across both lists.
  const filteredArchivedBots = useMemo(
    () => applyBotsFilter(archivedBots, filter),
    [archivedBots, filter]
  );
  const {
    overlays: botOverlays,
    weeklyDeltas: botWeeklyDeltas,
  } = useRealBotDetailsBulk(overlayBotIds);

  const scopedBots = useMemo<BotT[]>(() => {
    if (kind === "real" && realBotsAll && realBotsAll.length > 0) {
      const allowed = new Set(account.botIds);
      // No botIds on the account → render no bots. Sergey 2026-04-30:
      // the previous "fall through to all real bots" defensive path
      // surfaced PAPER-account bots in the LIVE-account view when the
      // user had deleted all their live bots — `realBotsAll` is the
      // user's full bot list across live AND paper accounts, so
      // unfiltered it drags paper bots in. Aggregate sentinel
      // populates `botIds` from the live-accounts union; real per-
      // account detail responses populate from the broker's bot list.
      // If both are empty, that's the truth — show nothing.
      const filtered = realBotsAll.filter((b) => allowed.has(b.id));
      // Apply the rich overlay sourced from `/bots/{id}`:
      //   • currentValue ← portfolio.equity (real value)
      //   • buyingPower ← portfolio.buyingPower
      //   • allTimeGain / allTimeGainPct ← portfolio.totalProfit /
      //                                      totalProfitPercent
      //   • dayChangePct ← latest two states[] rows
      //   • twrPct, apyPct, sharpe, maxDDPct ← computed from states[]
      // Each field falls back to the existing /bots-list value when
      // the overlay didn't populate it (cache-only first paint).
      return filtered.map((b) => {
        const o = botOverlays[b.id];
        if (!o) return b;
        const currentValue =
          typeof o.value === "number" ? o.value : b.currentValue;
        const allTimeGain =
          typeof o.totalReturnUsd === "number"
            ? o.totalReturnUsd
            : currentValue - b.allocatedAmount;
        const allTimeGainPct =
          typeof o.totalReturnFraction === "number"
            ? o.totalReturnFraction
            : b.allocatedAmount > 0
              ? allTimeGain / b.allocatedAmount
              : 0;
        return {
          ...b,
          currentValue,
          allTimeGain,
          allTimeGainPct,
          performancePct: allTimeGainPct,
          buyingPower:
            typeof o.buyingPower === "number" ? o.buyingPower : b.buyingPower,
          dayChangePct:
            typeof o.dayChangeFraction === "number"
              ? o.dayChangeFraction
              : b.dayChangePct,
          // TWR ≈ totalProfitFraction for bots without mid-stream
          // cashflow (the typical case — bots are funded once with an
          // allocation and let run). This produces a meaningful TWR
          // until we wire in proper cashflow-aware time-weighting.
          twrPct:
            typeof o.totalReturnFraction === "number"
              ? o.totalReturnFraction
              : b.twrPct,
          apyPct:
            typeof o.apyFraction === "number" ? o.apyFraction : b.apyPct,
          sharpe: typeof o.sharpe === "number" ? o.sharpe : b.sharpe,
          maxDDPct:
            typeof o.maxDDFraction === "number" ? o.maxDDFraction : b.maxDDPct,
          // Sparkline + tickers come from /bots/{id}.states[] and
          // .stocks[] — without them, real bot rows look very
          // different from demo bot rows (no sparkline, empty
          // universe). Using the overlay value when present unifies
          // the rendering.
          sparkline:
            o.sparkline && o.sparkline.length > 0
              ? o.sparkline
              : b.sparkline,
          tickers:
            o.tickers && o.tickers.length > 0 ? o.tickers : b.tickers,
        };
      });
    }
    return bots;
  }, [kind, realBotsAll, account.botIds, bots, botOverlays]);

  // Re-sort / re-filter only when inputs change. Small list (≤ 10 bots
  // per account in the demo) but the memo saves the derived-metrics
  // work on every keystroke in the search box.
  const filteredBots = useMemo(
    () => applyBotsFilter(scopedBots, filter),
    [scopedBots, filter]
  );

  // Advanced view's sortable headers route through here — we
  // mirror the filter bar's toggle logic (tap same key flips direction)
  // so both controls stay in sync.
  const handleSortFromHeader = useCallback(
    (key: BotsFilterState["sortKey"]) => {
      setFilter((prev) =>
        prev.sortKey === key
          ? {
              ...prev,
              sortDir: prev.sortDir === "asc" ? "desc" : "asc"
            }
          : {
              ...prev,
              sortKey: key,
              sortDir: key === "maxDDPct" || key === "ageDays" ? "asc" : "desc"
            }
      );
    },
    []
  );

  // Tap on a row → open drawer instead of route-pushing to /bots/:id.
  // The drawer's "View bot page" CTA still routes to that page when
  // the user wants the full read. Defined ABOVE the early-return so
  // the hook order stays stable across renders (rules-of-hooks).
  const openDetail = useCallback(
    (id: string) => {
      const found =
        filteredBots.find((b) => b.id === id) ??
        filteredArchivedBots.find((b) => b.id === id) ??
        null;
      setDetailBot(found);
    },
    [filteredBots, filteredArchivedBots]
  );

  if (scopedBots.length === 0 && archivedBots.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No bots attached to this account yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        pt: { xs: 1, xl: 0 },
        height: { xl: "100%" },
        display: { xl: "flex" },
        flexDirection: { xl: "column" },
        minHeight: { xl: 0 },
      }}
    >
      <Box
        sx={{
          position: { md: "sticky", xl: "static" },
          top: { md: 120 },
          zIndex: { md: 4 },
          bgcolor: { md: "background.default" },
          // xl: 2px breathing space between the tab strip's underline
          // and the search box — Sergey 2026-05-13.
          pt: { md: 1, xl: "2px" },
          flexShrink: { xl: 0 },
        }}
      >
        <BotsFilterBar filter={filter} onChange={setFilter} sx={{ mb: 1.5 }} />
      </Box>
      <Box
        sx={{
          flex: { xl: 1 },
          minHeight: { xl: 0 },
          overflowY: { xl: "auto" },
          ...autoHideScrollbarSx,
        }}
      >
      {scopedBots.length === 0 ? (
        <Box sx={{ py: 2, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No active bots — all of them are archived.
          </Typography>
        </Box>
      ) : filter.mode === "simple" ? (
        <BotsSimpleView
          bots={filteredBots}
          weeklyDeltas={botWeeklyDeltas}
          onBotClick={openDetail}
        />
      ) : (
        <BotsAdvancedView
          bots={filteredBots}
          sortKey={filter.sortKey}
          sortDir={filter.sortDir}
          onSort={handleSortFromHeader}
          onBotClick={openDetail}
        />
      )}
      {/* Archived bots — collapsed-by-default expandable below the
       *  active list. Sergey 2026-04-30: "after all active bots if any,
       *  show 'Show archived bots' and show the metrics and balances on
       *  the day it was deleted". Sorted newest-archived first inside
       *  the hook. Same Simple/Advanced view as active so the user's
       *  filter + sort apply consistently. */}
      {archivedBots.length > 0 && (
        <Box sx={{ mt: scopedBots.length > 0 ? 2 : 0 }}>
          <Button
            onClick={() => setArchivedExpanded((v) => !v)}
            color="inherit"
            // Sergey 2026-04-30: bumped font + removed size="small" so
            // the affordance matches the visual weight of "Buying power
            // (excl. bots) >" above — both are section-level navigation
            // links and should read at the same scale.
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1rem",
              color: "text.secondary",
              px: 1
            }}
            endIcon={
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  transform: archivedExpanded ? "rotate(180deg)" : "none",
                  transition: "transform 200ms ease",
                  fontSize: "0.85em",
                  lineHeight: 1
                }}
              >
                ▾
              </Box>
            }
          >
            {archivedExpanded ? "Hide" : "Show"} {archivedBots.length} archived bot
            {archivedBots.length === 1 ? "" : "s"}
          </Button>
          {archivedExpanded && (
            <Box sx={{ mt: 1, opacity: 0.85 }}>
              {filter.mode === "simple" ? (
                <BotsSimpleView
                  bots={filteredArchivedBots}
                  weeklyDeltas={{}}
                  onBotClick={openDetail}
                />
              ) : (
                <BotsAdvancedView
                  bots={filteredArchivedBots}
                  sortKey={filter.sortKey}
                  sortDir={filter.sortDir}
                  onSort={handleSortFromHeader}
                  onBotClick={openDetail}
                />
              )}
            </Box>
          )}
        </Box>
      )}
      </Box>{/* /scroll wrapper */}
      <BotDetailDrawer
        bot={detailBot}
        cohort={[...filteredBots, ...filteredArchivedBots]}
        accounts={isAggregate ? liveAccounts : [account]}
        showAccount={isAggregate}
        weeklyDeltas={botWeeklyDeltas}
        botOverlays={botOverlays}
        archivedCurves={archivedCurves}
        archivedOverrides={archivedOverrides}
        onArchivedOverrideChange={setArchivedOverride}
        onClose={() => setDetailBot(null)}
      />
    </Box>
  );
}

/**
 * Uppercase muted section header used inside the account-selector Menu
 * to group Live vs Paper accounts. Not a MenuItem — rendered as a
 * plain `li` with pointer events off so the user can't "select" it.
 */
function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="li"
      sx={{
        listStyle: "none",
        px: 2,
        pt: 1.25,
        pb: 0.5,
        pointerEvents: "none"
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: "uppercase"
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function PlaceholderPanel({ heading, body }: { heading: string; body: string }) {
  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="overline" color="text.secondary">{heading}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{body}</Typography>
    </Box>
  );
}

/**
 * Orders tab body — glue between the filter bar and the two views.
 *
 * State ownership:
 *  • Filter state lives here so Simple and Advanced see the same
 *    narrowed list when the user flips the segmented control. If it
 *    lived inside each view component, the filters would reset on
 *    every toggle.
 *  • The underlying list is derived from the account id — filtering
 *    `ORDERS` upstream of the filter bar so the bar's bot menu only
 *    offers bots actually attached to this account (via `bots` prop
 *    passed in from the tabs wrapper).
 *
 * `useMemo` keys: we recompute the filtered list any time either the
 * source orders or the filter state changes. This is a small list
 * (~50 rows per account) so the memo is mostly for preventing the
 * table from rerendering every keystroke in the search input.
 */
function OrdersPanel({
  account,
  bots,
  isAggregate,
  liveAccounts
}: {
  account: BrokerAccount;
  bots: BotT[];
  isAggregate: boolean;
  /** Hydrated live accounts list. Enables the real-session aggregate
   *  fan-out; pass empty for demo sessions. */
  liveAccounts: readonly BrokerAccount[];
}) {
  const [filter, setFilter] = useState<OrdersFilterState>(DEFAULT_ORDERS_FILTER);

  // Stable patch applier — the order detail panel surfaces value-tap
  // handlers ("tap ticker → filter by ticker") that merge a partial
  // filter state into the live filter. We wrap `setFilter` in
  // `useCallback` so the row components don't re-render unnecessarily
  // when only the orders list changes.
  const handleApplyFilter = useCallback(
    (patch: Partial<OrdersFilterState>) =>
      setFilter((prev) => ({ ...prev, ...patch })),
    []
  );

  // Real-session order sources. Both hooks run unconditionally (React
  // requires it) but idle when the session/account kind doesn't match.
  const kind = useAuthStore((s) => s.kind);
  const isRealSingle = kind === "real" && !isAggregate && account.source === "real";
  const { orders: realSingleOrders } = useRealAccountOrders(
    isRealSingle ? account.id : null
  );
  const { orders: realLiveOrders } = useRealLiveAccountOrders(
    isAggregate ? liveAccounts : []
  );

  // Full real-bot directory for name resolution. The `bots` prop above
  // is scoped to `account.botIds`, which means orders referencing a
  // bot that's been removed from the account (or that lives on another
  // account) fall back to "Bot · {uuid suffix}" in the detail drawer.
  // Pulling the unscoped /bots cache and merging gives the lookup a
  // wider net so the user sees real names everywhere.
  // 2026-04-25 Sergey: real bot names must always render in the order
  // drawer; UUIDs are unreadable.
  const { bots: realBotsAll } = useRealBots();
  const botsForView = useMemo<BotT[]>(() => {
    if (kind !== "real" || !realBotsAll || realBotsAll.length === 0) {
      return bots;
    }
    // Dedup by id, keeping the account-scoped entry first (it carries
    // accountId, which the row UI sometimes uses for filter-by-account
    // affordances).
    const byId = new Map<string, BotT>();
    for (const b of bots) byId.set(b.id, b);
    for (const b of realBotsAll) if (!byId.has(b.id)) byId.set(b.id, b);
    return Array.from(byId.values());
  }, [kind, bots, realBotsAll]);

  // Accounts list fed to the filter bar + child views. Under a real
  // session we want those components to pick accounts from the
  // hydrated `liveAccounts` prop (so "filter by account" matches real
  // ids); otherwise fall back to MOCK_ACCOUNTS for demo parity.
  //
  // The spread-copy is deliberate: `liveAccounts` arrives typed as
  // `readonly BrokerAccount[]` (immutability marker threaded down from
  // the parent), but the Orders* child components predate that prop
  // and still expect a mutable `BrokerAccount[]`. Rather than bump
  // every child's prop type for what is a read-only contract anyway,
  // we just hand them a fresh mutable copy here.
  const viewAccounts: BrokerAccount[] =
    kind === "real" && liveAccounts.length > 0
      ? [...liveAccounts]
      : MOCK_ACCOUNTS;

  // Scope to this account first — saves the filter's per-row passes
  // from walking orders that could never match.
  //
  // Aggregate mode (`isAggregate`): the selected "account" is the
  // virtual __all_live__ sentinel, which no order's `accountId` matches.
  // Expand the scope to every live account's orders so the consolidated
  // Orders view actually shows rows. We build a Set once per selection
  // for O(1) membership checks — cheap, but matters as order lists grow
  // with additional live accounts.
  //
  // Real sessions use /trading-history results; demo uses the seeded
  // mock dataset.
  const accountOrders = useMemo(() => {
    if (isAggregate) {
      // Real session: use the real fan-out result regardless of
      // length. Empty array is the truth for an account that's
      // genuinely got no orders in the window. Sergey 2026-04-30:
      // gating on `length > 0` made a real-but-empty result fall
      // through to MOCK orders, contradicting what the per-account
      // view showed.
      if (kind === "real") {
        return realLiveOrders;
      }
      const liveIds = new Set(
        MOCK_ACCOUNTS.filter((a) => !a.paper).map((a) => a.id)
      );
      return ORDERS.filter((o) => liveIds.has(o.accountId));
    }
    if (isRealSingle && realSingleOrders) {
      return realSingleOrders;
    }
    return ORDERS.filter((o) => o.accountId === account.id);
  }, [
    account.id,
    isAggregate,
    kind,
    isRealSingle,
    realSingleOrders,
    realLiveOrders,
  ]);

  // Live company / ETF names for every ticker that appears in the
  // visible orders. Sergey 2026-04-26: the static ticker→name map
  // can never keep up with every symbol an Alpaca account holds, so
  // pull names from the same /api/market/quotes pipeline the chart
  // already uses. The hook dedupes by sorted-ticker key and reads
  // through IndexedDB, so this call piggybacks on the historical-
  // prices fetch and adds zero new server requests when the cache
  // is warm. Names cached per-ticker for 24h alongside prices.
  const orderTickers = useMemo(() => {
    const seen = new Set<string>();
    for (const o of accountOrders) {
      if (typeof o.asset === "string" && o.asset.length > 0) {
        seen.add(o.asset.toUpperCase());
      }
    }
    return Array.from(seen);
  }, [accountOrders]);
  const { names: liveTickerNames } = useRealHistoricalPrices(orderTickers);

  const filteredOrders = useMemo(
    () => applyOrdersFilter(accountOrders, filter),
    [accountOrders, filter]
  );

  return (
    <Box
      sx={{
        pt: { xs: 1, xl: 0 },
        // xl: flex column filling the inherited pane height; filter
        // sits static at top (flexShrink:0), list takes the rest with
        // its own internal scroll. md/lg: regular block, filter is
        // sticky on the page scroll. Sergey 2026-05-13: filter must
        // be part of the pinned chrome, not floating with a gap.
        height: { xl: "100%" },
        display: { xl: "flex" },
        flexDirection: { xl: "column" },
        minHeight: { xl: 0 },
      }}
    >
      <Box
        sx={{
          position: { md: "sticky", xl: "static" },
          top: { md: 120 },
          zIndex: { md: 4 },
          bgcolor: { md: "background.default" },
          // xl: 2px breathing space between the tab strip's underline
          // and the search box — Sergey 2026-05-13.
          pt: { md: 1, xl: "2px" },
          mb: { xs: 0, md: 1, xl: 0 },
          flexShrink: { xl: 0 },
        }}
      >
        <OrdersFilterBar
          filter={filter}
          onChange={setFilter}
          bots={botsForView}
          accounts={viewAccounts}
          showAccount={isAggregate}
          sx={{ mb: 1 }}
        />
      </Box>
      <Box
        sx={{
          flex: { xl: 1 },
          minHeight: { xl: 0 },
          overflowY: { xl: "auto" },
          ...autoHideScrollbarSx,
        }}
      >
        {filter.mode === "simple" ? (
          <OrdersSimpleView
            orders={filteredOrders}
            bots={botsForView}
            accounts={viewAccounts}
            showAccount={isAggregate}
            onApplyFilter={handleApplyFilter}
            // Unfiltered set used for FIFO realized-P/L computation in
            // the detail drawer. A user-applied date / status filter
            // would hide older buys and break cost-basis matching, so
            // the drawer always sees the full account order history.
            allAccountOrders={accountOrders}
            tickerNames={liveTickerNames}
          />
        ) : (
          <OrdersAdvancedView
            orders={filteredOrders}
            bots={botsForView}
            accounts={viewAccounts}
            showAccount={isAggregate}
            tickerNames={liveTickerNames}
          />
        )}
      </Box>
    </Box>
  );
}

/**
 * Positions tab content. Owns its filter state, resolves the set of
 * accounts to pull positions from (single account vs __all_live__
 * aggregate), and dispatches to Simple or Advanced view based on the
 * filter bar's `mode`.
 *
 * Mirrors BotsPanel + OrdersPanel structure so the three tabs behave
 * identically from a user perspective (filter bar on top, toggle between
 * Simple/Advanced sub-views).
 */
function PositionsPanel({
  account,
  isAggregate,
  liveAccounts
}: {
  account: BrokerAccount;
  isAggregate: boolean;
  /** Hydrated list of real+live accounts from the parent. Used to
   *  drive the aggregate "All Live" positions fan-out. Empty when the
   *  user is in a demo session. */
  liveAccounts: readonly BrokerAccount[];
}) {
  const [filter, setFilter] = useState<PositionsFilterState>(
    DEFAULT_POSITIONS_FILTER
  );

  // Real-session positions. For a single real account the detail
  // endpoint's `stocks[]` array carries the open holdings; for the
  // aggregate we fan out to every live real account and concatenate.
  // Both hooks run unconditionally (React requires it) but idle on
  // demo sessions / non-real accounts.
  const kind = useAuthStore((s) => s.kind);
  const isRealSingle = kind === "real" && !isAggregate && account.source === "real";
  const { positions: realSinglePositions } = useRealAccountPositions(
    isRealSingle ? account.id : null
  );
  // Aggregate hook filters internally to real+live, so passing the
  // full list is safe. Empty list outside aggregate mode to keep it
  // idle.
  const { positions: realLivePositions } = useRealLiveAccountPositions(
    isAggregate ? liveAccounts : []
  );

  // Scope to this account first. Same pattern as OrdersPanel — the
  // virtual __all_live__ sentinel never matches any position's
  // `accountId`, so in aggregate mode we fan out to every live account.
  //
  // Real sessions use the API-derived `stocks[]` array; demo sessions
  // fall back to the seeded mock dataset. Aggregate behaviour is the
  // same split (real fanout vs mock fanout).
  // Bot attribution — the broker's stocks[] response doesn't say
  // which bot owns each lot, so we infer ownership by matching the
  // position's ticker against each bot's currently-held stocks[]
  // (surfaced as `BotOverlay.tickers` from `/bots/{id}.stocks[]`).
  // For aggregate "All Live", we attribute across every bot in the
  // live cohort; for a single account, just that account's bots.
  // Demo positions already carry `botId` from the seed and are
  // passed through unchanged.
  const botIdsForAttribution = useMemo(() => {
    if (kind !== "real") return [] as string[];
    if (isAggregate) {
      const set = new Set<string>();
      for (const a of liveAccounts) {
        for (const id of a.botIds) set.add(id);
      }
      return Array.from(set).sort();
    }
    return [...account.botIds].sort();
  }, [kind, isAggregate, liveAccounts, account.botIds]);

  const { overlays: positionBotOverlays } = useRealBotDetailsBulk(
    botIdsForAttribution
  );

  // Ticker → bot ids that currently hold it. Built once from the
  // overlay map; same lookup answers both single-account and
  // aggregate questions (a ticker can be held by multiple bots
  // either because the user runs identical strategies in different
  // accounts or because two strategies converged on the same name).
  const tickerToBotIds = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const [botId, overlay] of Object.entries(positionBotOverlays)) {
      if (!overlay.tickers) continue;
      for (const t of overlay.tickers) {
        if (typeof t !== "string" || !t) continue;
        const key = t.toUpperCase();
        (out[key] ??= []).push(botId);
      }
    }
    return out;
  }, [positionBotOverlays]);

  const scopedPositions = useMemo(() => {
    // 1. Pull the right slice of positions for this view (single
    //    account, real-single, mock single, or aggregate fan-out).
    //
    //    Real-session aggregate: ALWAYS use the real fan-out, even
    //    when it returns an empty array. Sergey 2026-04-30: previous
    //    code gated on `length > 0`, so a real user with zero
    //    positions (deleted bot, sold everything) silently fell
    //    through to MOCK_POSITIONS (AAPL, MSFT, NVDA, GOOGL,
    //    META…) — which read as "the aggregate has demo positions
    //    but the single account is empty". Real users see the
    //    truth: empty positions render the existing "no positions"
    //    empty state.
    let raw: Position[] = [];
    if (isAggregate) {
      if (kind === "real") {
        raw = realLivePositions;
      } else {
        const liveIds = MOCK_ACCOUNTS.filter((a) => !a.paper).map((a) => a.id);
        raw = positionsForAccounts(liveIds);
      }
    } else if (isRealSingle && realSinglePositions) {
      raw = realSinglePositions;
    } else {
      raw = positionsForAccounts([account.id]);
    }
    // 2. In All-Live aggregate ACROSS MULTIPLE ACCOUNTS, the same
    //    ticker can appear once per account or per bot — V (VISA)
    //    shows up twice in Sergey's book. Roll those up into a
    //    single row so the user sees their actual exposure to the
    //    ticker, not duplicated rows they have to mentally sum.
    //    Drawer shows "Multiple bots / accounts" via botCount /
    //    accountCount fields.
    //
    //    When the aggregate has only ONE live account (Sergey's
    //    current case after deleting the bot — single Default Live
    //    is the entire "All Live" set), the merge is a no-op by
    //    construction but introduces synthesised ids and clears
    //    botId — so single-account All-Live diverged from the
    //    Selected-Live view in subtle ways (Sergey 2026-04-30).
    //    Skip the merge in that case so the two views render
    //    identically.
    const shouldMerge = isAggregate && liveAccounts.length > 1;
    const merged = shouldMerge ? mergePositionsByTicker(raw) : raw;
    // 3. Attribute each row to a bot via ticker → bot lookup. Skip
    //    rows that already carry a botId or botCount (demo seeds
    //    or the per-leg botId set by the original real adapter when
    //    the broker did surface attribution). Drawer renders:
    //      • bot.name when botId set (single owner)
    //      • "Multiple bots (N)" when botCount > 1
    //      • "Manual" otherwise
    return merged.map((p) => {
      if (p.botId || (p.botCount && p.botCount > 0)) return p;
      const owners = tickerToBotIds[p.asset.toUpperCase()] ?? [];
      if (owners.length === 1) {
        return { ...p, botId: owners[0], botCount: 1 };
      }
      if (owners.length > 1) {
        return { ...p, botCount: owners.length };
      }
      return p;
    });
  }, [
    account.id,
    isAggregate,
    kind,
    isRealSingle,
    realSinglePositions,
    realLivePositions,
    tickerToBotIds,
  ]);

  // Historical prices — needed for the per-position Today + This-week
  // P/L. The broker's detail response leaves `todayPLDollar` at 0 for
  // real positions (no per-day delta surfaced), so we synthesise it
  // from `current_price − prior_close`. Same waterfall the account-
  // level reconstruction uses (Massive / FMP / Yahoo).
  const positionTickers = useMemo(() => {
    const set = new Set<string>();
    for (const p of scopedPositions) {
      if (p.asset) set.add(p.asset.toUpperCase());
    }
    return Array.from(set).sort();
  }, [scopedPositions]);
  const { prices: positionPrices } = useRealHistoricalPrices(positionTickers);

  // Enrich scopedPositions with price-derived Today + Week deltas:
  //   • Today  = (current − last close before today) × qty       (signed by side)
  //   • Week   = (current − last close before this Mon) × qty    (signed by side)
  // Mirrors the "Today / This week / —" fallback chain BotsSimpleView
  // and BotDetailDrawer use. Demo positions already carry a non-zero
  // todayPLDollar (computed from the asset's snapshot dayPct) — we
  // only enrich when the broker-supplied value is exactly 0, so demo
  // screenshots don't drift.
  const positionsAndWeekly = useMemo(() => {
    const weekly: Record<string, { fraction: number; delta: number }> = {};
    if (Object.keys(positionPrices).length === 0) {
      return { positions: scopedPositions, weekly };
    }
    const todayIso = new Date().toISOString().slice(0, 10);
    const yesterdayIso = isoAddDays(todayIso, -1);
    // Monday-of-this-week 00:00 UTC; baseline for "this week" is the
    // last close strictly before Monday (typically last Friday).
    const monday = new Date(mondayOfThisWeekUTCMs());
    const mondayIso = monday.toISOString().slice(0, 10);
    const preMondayIso = isoAddDays(mondayIso, -1);

    const out = scopedPositions.map((p) => {
      const series = positionPrices[p.asset.toUpperCase()];
      if (!series || series.length === 0) return p;
      const cur = p.currentPrice;
      const sideMul = p.side === "long" ? 1 : -1;
      const yClose = closeOnOrBefore(series, yesterdayIso);
      const wClose = closeOnOrBefore(series, preMondayIso);

      // Weekly — always emit when we have a baseline.
      if (wClose !== null && wClose > 0 && Number.isFinite(cur)) {
        const delta = (cur - wClose) * p.qty * sideMul;
        const fraction = ((cur - wClose) / wClose) * sideMul;
        weekly[p.id] = { delta, fraction };
      }

      // Today — only synthesise when the broker didn't supply (real
      // positions hit 0; demo positions have non-zero from mock data).
      const needTodayBackfill = p.todayPLDollar === 0;
      if (
        needTodayBackfill &&
        yClose !== null &&
        yClose > 0 &&
        Number.isFinite(cur)
      ) {
        const todayDelta = (cur - yClose) * p.qty * sideMul;
        const todayPct = ((cur - yClose) / yClose) * sideMul;
        return {
          ...p,
          todayPLDollar: todayDelta,
          todayPLPct: todayPct,
          dayChangePct: (cur - yClose) / yClose
        };
      }
      return p;
    });
    return { positions: out, weekly };
  }, [scopedPositions, positionPrices]);

  const filteredPositions = useMemo(
    () => applyPositionsFilter(positionsAndWeekly.positions, filter),
    [positionsAndWeekly.positions, filter]
  );

  // Drawer state — same pattern BotsPanel uses. Holds the snapshot of
  // the position whose row was tapped; the drawer reads from this and
  // remains visible through its slide-out animation. Defined ABOVE the
  // early return so hook order stays stable across renders.
  const [detailPosition, setDetailPosition] = useState<Position | null>(null);
  const openDetail = useCallback(
    (id: string) => {
      const found = filteredPositions.find((p) => p.id === id) ?? null;
      setDetailPosition(found);
    },
    [filteredPositions]
  );

  // Bots list for resolving `position.botId → name` inside the drawer.
  // Real sessions get the live `/bots` list; demo falls back to the
  // seeded BOTS array. Defensive when the real fetch is in flight.
  const { bots: realBotsAll } = useRealBots();
  const botsForDrawer = useMemo(() => {
    if (kind === "real") return realBotsAll ?? [];
    return BOTS;
  }, [kind, realBotsAll]);

  // Advanced view's sortable headers flip direction on repeat tap and
  // switch key otherwise. Numeric columns (MV, P/L, qty, etc) default
  // to "desc" so the first tap surfaces the largest/best rows; the
  // ticker column defaults to "asc" since alphabetical-forward is the
  // expected first direction for a name column.
  const handleSortFromHeader = useCallback(
    (key: PositionsFilterState["sortKey"]) => {
      setFilter((prev) =>
        prev.sortKey === key
          ? { ...prev, sortDir: prev.sortDir === "asc" ? "desc" : "asc" }
          : {
              ...prev,
              sortKey: key,
              sortDir: key === "ticker" ? "asc" : "desc"
            }
      );
    },
    []
  );

  if (scopedPositions.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No positions open for this account yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        pt: { xs: 1, xl: 0 },
        height: { xl: "100%" },
        display: { xl: "flex" },
        flexDirection: { xl: "column" },
        minHeight: { xl: 0 },
      }}
    >
      <Box
        sx={{
          position: { md: "sticky", xl: "static" },
          top: { md: 120 },
          zIndex: { md: 4 },
          bgcolor: { md: "background.default" },
          // xl: 2px breathing space between the tab strip's underline
          // and the search box — Sergey 2026-05-13.
          pt: { md: 1, xl: "2px" },
          flexShrink: { xl: 0 },
        }}
      >
        <PositionsFilterBar filter={filter} onChange={setFilter} sx={{ mb: 1.5 }} />
      </Box>
      <Box
        sx={{
          flex: { xl: 1 },
          minHeight: { xl: 0 },
          overflowY: { xl: "auto" },
          ...autoHideScrollbarSx,
        }}
      >
        {filter.mode === "simple" ? (
          <PositionsSimpleView
            positions={filteredPositions}
            weeklyDeltas={positionsAndWeekly.weekly}
            onPositionClick={openDetail}
          />
        ) : (
          <PositionsAdvancedView
            positions={filteredPositions}
            sortKey={filter.sortKey}
            sortDir={filter.sortDir}
            onSort={handleSortFromHeader}
            onPositionClick={openDetail}
          />
        )}
      </Box>
      <PositionDetailDrawer
        position={detailPosition}
        bots={botsForDrawer}
        accounts={isAggregate ? liveAccounts : [account]}
        showAccount={isAggregate}
        weeklyDeltas={positionsAndWeekly.weekly}
        onClose={() => setDetailPosition(null)}
      />
    </Box>
  );
}

/**
 * Balances tab — wraps `BalancesView` with the single/aggregate account
 * resolution logic that the rest of this page already uses for Orders,
 * Bots, and Positions.
 *
 * In aggregate ("All Live") mode we feed every live account into the
 * view (real-aware: pulls from `liveAccounts` which is real or mock
 * depending on session) and overlay each bot's current portfolio value
 * from `/bots/{id}.portfolio.equity` — without that overlay,
 * `Excl. bots` mode subtracts zero (bot.currentValue defaults to 0
 * from the `/bots` list endpoint until the per-bot detail lands).
 *
 * Sergey 2026-04-27: "We need to calculate Excl.bots. The amounts
 * must be adjusted for Bots Portfolio Value, do not forget All Live
 * Accounts" — fixed both the aggregate accounts wiring (was MOCK
 * for real sessions) and the bot value pipeline.
 */
function BalancesPanel({
  account,
  bots,
  isAggregate,
  liveAccounts
}: {
  account: BrokerAccount;
  bots: BotT[];
  isAggregate: boolean;
  liveAccounts: readonly BrokerAccount[];
}) {
  const kind = useAuthStore((s) => s.kind);

  const accounts = useMemo(
    () => (isAggregate ? [...liveAccounts] : [account]),
    [account, isAggregate, liveAccounts]
  );

  // Bot ids in scope. In aggregate, the union of all live accounts'
  // bots; in single, just this account's bots.
  const botIdsInScope = useMemo(() => {
    if (isAggregate) {
      const set = new Set<string>();
      for (const a of liveAccounts) {
        for (const id of a.botIds) set.add(id);
      }
      return Array.from(set).sort();
    }
    return [...account.botIds].sort();
  }, [isAggregate, liveAccounts, account.botIds]);

  // Pull the overlays so we get `/bots/{id}.portfolio.equity` →
  // `BotOverlay.value`. Cache-hot when BotsPanel already requested
  // the same set this session.
  const { overlays: botOverlays } = useRealBotDetailsBulk(botIdsInScope);

  // For aggregate mode, the seed `bots` prop is scoped to the
  // currently-selected (sentinel) account; we want every bot across
  // every live account. Pull `useRealBots()` + filter to the in-
  // scope ids, falling back to the seed `bots` for demo / mock
  // sessions.
  const { bots: realBotsAll } = useRealBots();
  const baseBotsForBalance = useMemo(() => {
    if (kind !== "real") return bots;
    if (!isAggregate) return bots;
    if (!realBotsAll) return bots;
    const allowed = new Set(botIdsInScope);
    return realBotsAll.filter((b) => allowed.has(b.id));
  }, [kind, isAggregate, realBotsAll, botIdsInScope, bots]);

  // Bot → real-account id index. Required because in aggregate mode
  // the `bots` prop carries `accountId = __all_live__` (synthetic
  // sentinel from `useRealBotsForAccount`), and `deriveBalance`'s
  // per-account filter `b.accountId === account.id` won't match.
  // Rebuild from each real account's `botIds` so the filter sees the
  // true brokerage attribution. Works for both single and aggregate.
  const botToAccount = useMemo(() => {
    const m: Record<string, string> = {};
    const sources = isAggregate ? liveAccounts : [account];
    for (const a of sources) {
      for (const id of a.botIds) m[id] = a.id;
    }
    return m;
  }, [isAggregate, liveAccounts, account]);

  // Apply the overlay so `bot.currentValue` AND `bot.buyingPower`
  // reflect the real `/bots/{id}.portfolio.{equity,buyingPower}`
  // values. Without `buyingPower`, the `/bots` list mapper returns 0
  // and `Excl. bots` ends up subtracting nothing — the visible bug
  // Sergey caught with Total === Excl. on the 2-account view.
  // Also re-stamps `accountId` from the index above so the
  // per-account filter inside `deriveBalance` actually matches.
  const botsForBalance = useMemo<BotT[]>(() => {
    return baseBotsForBalance.map((b) => {
      const o = botOverlays[b.id];
      const value =
        o && typeof o.value === "number" && Number.isFinite(o.value)
          ? o.value
          : b.currentValue;
      const buyingPower =
        o &&
        typeof o.buyingPower === "number" &&
        Number.isFinite(o.buyingPower)
          ? o.buyingPower
          : b.buyingPower;
      // Bot's idle cash from `/bots/{id}.portfolio.cash` — feeds the
      // `Cash excl bots` calculation. Falls through to existing
      // `b.cash` (demo seeds may set it) and finally undefined; the
      // consumer treats undefined as "skip this bot's contribution".
      const cash =
        o && typeof o.cash === "number" && Number.isFinite(o.cash)
          ? o.cash
          : b.cash;
      return {
        ...b,
        accountId: botToAccount[b.id] ?? b.accountId,
        currentValue: value,
        buyingPower,
        cash
      } as BotT;
    });
  }, [baseBotsForBalance, botOverlays, botToAccount]);

  return (
    <Box sx={{ pt: 1 }}>
      <BalancesView
        accounts={accounts}
        bots={botsForBalance}
        isAggregate={isAggregate}
      />
    </Box>
  );
}

/**
 * Activities tab — Robinhood-style history of deposits, trades,
 * dividends, fees, and interest. Scopes to a single account or fans
 * out to every live account in aggregate ("All Live") mode, matching
 * the Orders / Positions / Balances pattern.
 *
 * The Simple view (cards grouped by Pending / month) is the only mode
 * we ship here — per Sergey's pick when designing this feature. An
 * Alpaca-style dense table is the natural follow-up but would add
 * weight without a clear mobile win right now.
 */
function ActivitiesPanel({
  account,
  isAggregate,
  liveAccounts
}: {
  account: BrokerAccount;
  isAggregate: boolean;
  liveAccounts: readonly BrokerAccount[];
}) {
  const [filter, setFilter] = useState<ActivitiesFilterState>(
    DEFAULT_ACTIVITIES_FILTER
  );

  // Real-session activities — single-account hook for the per-account
  // selection; aggregate hook for "All Live". Both idle on demo
  // sessions so paper accounts continue to use the mock fan-out
  // below.
  const realAccountId =
    account.source === "real" && !isAggregate ? account.id : null;
  const { data: realActivitiesRaw } = useRealAccountActivities(realAccountId);
  const { activities: realLiveActivitiesRaw } = useRealLiveAccountActivities(
    isAggregate ? liveAccounts : []
  );
  const realActivities = useMemo(() => {
    // Aggregate path: each raw row already carries its true
    // brokerage `accountId` (stamped by `useRealLiveAccountActivities`
    // at fetch time). Group by accountId and run the mapper once per
    // account so the resulting `Activity` rows preserve real
    // attribution — the mapper assigns its second-arg accountId
    // uniformly across all input rows. Concat + re-sort newest-first.
    if (isAggregate && realLiveActivitiesRaw.length > 0) {
      const byAcct = new Map<string, RawAccountActivity[]>();
      for (const a of realLiveActivitiesRaw) {
        if (!byAcct.has(a.accountId)) byAcct.set(a.accountId, []);
        byAcct.get(a.accountId)!.push(a);
      }
      const all = [];
      for (const [acctId, rows] of byAcct.entries()) {
        all.push(...mapRealActivitiesToActivities(rows, acctId));
      }
      return all.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
    }
    if (realActivitiesRaw) {
      return mapRealActivitiesToActivities(realActivitiesRaw, account.id);
    }
    return null;
  }, [isAggregate, realLiveActivitiesRaw, realActivitiesRaw, account.id]);

  // Mock fallback. Only hit when neither real path returned data
  // (i.e. demo session, or paper-account view).
  const scoped = useMemo(() => {
    if (realActivities) return realActivities;
    if (isAggregate) {
      // Module-scope child panel — reads mock set directly (see R13 note).
      const liveIds = MOCK_ACCOUNTS.filter((a) => !a.paper).map((a) => a.id);
      return activitiesForAccounts(liveIds);
    }
    return activitiesForAccounts([account.id]);
  }, [account.id, isAggregate, realActivities]);

  const filtered = useMemo(
    () => applyActivitiesFilter(scoped, filter),
    [scoped, filter]
  );

  return (
    <Box
      sx={{
        pt: { xs: 1, xl: 0 },
        height: { xl: "100%" },
        display: { xl: "flex" },
        flexDirection: { xl: "column" },
        minHeight: { xl: 0 },
      }}
    >
      <Box
        sx={{
          position: { md: "sticky", xl: "static" },
          top: { md: 120 },
          zIndex: { md: 4 },
          bgcolor: { md: "background.default" },
          // xl: 2px breathing space between the tab strip's underline
          // and the search box — Sergey 2026-05-13.
          pt: { md: 1, xl: "2px" },
          flexShrink: { xl: 0 },
        }}
      >
        <ActivitiesFilterBar filter={filter} onChange={setFilter} sx={{ mb: 1.5 }} />
      </Box>
      <Box
        sx={{
          flex: { xl: 1 },
          minHeight: { xl: 0 },
          overflowY: { xl: "auto" },
          ...autoHideScrollbarSx,
        }}
      >
        <ActivitiesSimpleView activities={filtered} showAccount={isAggregate} />
      </Box>
    </Box>
  );
}

/**
 * Buying Power toggle — V2 version.
 *
 * Round-4 redesign per Sergey:
 *  • Bigger typography — label sits on its own line as a proper section
 *    lead-in (body1 ~1rem), value underneath at the same size as the
 *    other big readouts so it scans at arm's length instead of being a
 *    footnote-sized secondary line.
 *  • Explicit swap icon to flip between "excl. bots" ↔ "total" —
 *    previously the whole row was clickable but there was no visible
 *    affordance telling the user there *were* two modes.
 *  • Chevron-right on the far right — this whole row is on the roadmap
 *    to navigate to a dedicated Buying Power detail page. Clicking the
 *    row (or the chevron) pushes that route; the swap icon is its own
 *    click target (stopPropagation) so users can flip modes without
 *    accidentally leaving the page.
 *
 * Round-8 (Balances): the "detail page" affordance now opens a
 * bottom-sheet `BalancesDrawer` instead of navigating to a separate
 * placeholder route. The drawer hosts `BalancesView` in compact mode,
 * giving users the full Alpaca-style balance breakdown (cash, margin,
 * positions, high-yield interest, misc) without losing their place on
 * the Accounts page. The same view is also reachable via the "Balances"
 * tab below — the two entry points render the same component; the tab
 * shows the hero, the drawer uses compact mode since the hero's role is
 * already being played by the original row the user tapped.
 */
function BuyingPowerToggle({
  account,
  bots,
  isAggregate,
  liveAccounts
}: {
  account: BrokerAccount;
  bots: BotT[];
  isAggregate: boolean;
  /** All live accounts in scope — required so aggregate mode feeds the
   *  same dataset to BalancesView/deriveBalance that the Balances tab
   *  uses. Without this the drawer fell back to MOCK_ACCOUNTS, which
   *  for real sessions silently rendered demo balances. */
  liveAccounts: readonly BrokerAccount[];
}) {
  const [showTotal, setShowTotal] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const label = showTotal ? "Buying power (total)" : "Buying power (excl. bots)";

  // The accounts array the drawer should feed into BalancesView. Mirrors
  // BalancesPanel's logic so the drawer and the tab present the exact
  // same dataset for a given hero state. Real-aware via the
  // `liveAccounts` prop so real sessions don't fall back to MOCK_ACCOUNTS.
  const drawerAccounts = useMemo(
    () => (isAggregate ? [...liveAccounts] : [account]),
    [account, isAggregate, liveAccounts]
  );

  // ---- Bot overlay + derived hero value ---------------------------------
  //
  // The page hero used to read `account.buyingPowerExBots` directly,
  // but the adapter sets that field equal to `buyingPower` because
  // it has no per-bot detail to subtract. Result: the hero showed
  // the same number as Total mode, contradicting the BalancesView
  // figure inside the drawer (which correctly subtracts allocations).
  //
  // Fix: run the same `deriveBalance` flow that BalancesView uses,
  // sharing one source of truth. We apply the bulk bot detail overlay
  // here so `bot.buyingPower` and `bot.currentValue` come from
  // `/bots/{id}` rather than the list-row stub (which sets BP = 0).
  // The overlaid bots are also passed into the drawer's BalancesView,
  // so the drawer's internal hero derivation lands on the same
  // number — Sergey 2026-04-29: "Please fix/synchronize numbers
  // under Buying Power and its drawer".
  const botIdsInScope = useMemo(() => {
    if (isAggregate) {
      const set = new Set<string>();
      for (const a of liveAccounts) for (const id of a.botIds) set.add(id);
      return Array.from(set).sort();
    }
    return [...account.botIds].sort();
  }, [isAggregate, liveAccounts, account.botIds]);
  const { overlays: botOverlays } = useRealBotDetailsBulk(botIdsInScope);

  // Bot → real-account id index — same purpose as in BalancesPanel:
  // sentinel `__all_live__` accountId on aggregate-mode bots prevents
  // `deriveBalance`'s per-account filter from matching, so we restamp
  // it from each real account's `botIds` list.
  const botToAccount = useMemo(() => {
    const m: Record<string, string> = {};
    const sources = isAggregate ? liveAccounts : [account];
    for (const a of sources) for (const id of a.botIds) m[id] = a.id;
    return m;
  }, [isAggregate, liveAccounts, account]);

  const overlaidBots = useMemo<BotT[]>(() => {
    return bots.map((b) => {
      const o = botOverlays[b.id];
      const value =
        o && typeof o.value === "number" && Number.isFinite(o.value)
          ? o.value
          : b.currentValue;
      const buyingPower =
        o &&
        typeof o.buyingPower === "number" &&
        Number.isFinite(o.buyingPower)
          ? o.buyingPower
          : b.buyingPower;
      const cash =
        o && typeof o.cash === "number" && Number.isFinite(o.cash)
          ? o.cash
          : b.cash;
      return {
        ...b,
        accountId: botToAccount[b.id] ?? b.accountId,
        currentValue: value,
        buyingPower,
        cash
      } as BotT;
    });
  }, [bots, botOverlays, botToAccount]);

  // Compute the hero number through the same `deriveBalance` flow that
  // BalancesView uses internally. Snapshot keyed by mode so `value`
  // flips instantly when the user toggles Total ↔ Excl. bots.
  const value = useMemo(() => {
    const mode = showTotal ? "total" : "exBots";
    const perAccount = drawerAccounts.map((a) =>
      deriveBalanceSnapshot(a, overlaidBots, mode)
    );
    if (perAccount.length === 0) return 0;
    const snap = aggregateBalanceSnapshots(perAccount);
    return snap.current.effectiveBuyingPower;
  }, [drawerAccounts, overlaidBots, showTotal]);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          cursor: "pointer",
          userSelect: "none",
          py: 0.5
        }}
        onClick={() => setDrawerOpen(true)}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Label + chevron on one line — round-5 feedback moved the
           *  chevron from the row's far right to sit right next to the
           *  label so it reads as an affordance on the *name* ("tap the
           *  Buying Power section to open its page") rather than a
           *  generic "open detail" cue at the edge of the card. */}
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                lineHeight: 1.25
              }}
            >
              {label}
            </Typography>
            <ChevronRightRounded
              sx={{ color: "text.secondary", fontSize: 20, flexShrink: 0 }}
            />
          </Stack>
          <Typography
            variant="body1"
            sx={{
              color: "text.primary",
              fontWeight: 700,
              fontFeatureSettings: '"tnum" 1',
              lineHeight: 1.25,
              // Round-7: breathing room between the small muted label
              // and the bold value — matches the Robinhood list-row
              // rhythm where "Buying Power" sits up top and the $
              // figure has its own visual landing space underneath
              // instead of being glued right below the label.
              mt: 0.75
            }}
          >
            {formatUSD(value)}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label={`Switch to ${showTotal ? "excluding bots" : "total"} buying power`}
          onClick={(e) => { e.stopPropagation(); setShowTotal((v) => !v); }}
          sx={{ p: 0.5, color: "text.secondary" }}
        >
          <SwapHorizRounded sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      <BalancesDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        accounts={drawerAccounts}
        bots={overlaidBots}
        isAggregate={isAggregate}
        heroValue={value}
        heroLabel={label}
      />
    </>
  );
}

/**
 * Bottom-sheet drawer that surfaces `BalancesView` in compact mode.
 * Opened by tapping the hero "Buying power" row. The header mirrors
 * the figure the user just tapped so they land in the drawer without
 * context loss — then BalancesView fills in the breakdown.
 */
function BalancesDrawer({
  open,
  onClose,
  accounts,
  bots,
  isAggregate,
  heroValue,
  heroLabel
}: {
  open: boolean;
  onClose: () => void;
  accounts: BrokerAccount[];
  bots: BotT[];
  isAggregate: boolean;
  heroValue: number;
  heroLabel: string;
}) {
  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom)",
          // Cap the drawer height so the user can still see a sliver
          // of the page behind it — standard bottom-sheet UX that
          // reinforces "this is a peek, not a page".
          maxHeight: "92vh",
          ...desktopBottomDrawerPaperSx,
        }
      }}
    >
      {/* Grab handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
        <Box
          sx={(t: Theme) => ({
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: t.palette.divider
          })}
        />
      </Box>

      {/* Header echo — same label + $ figure as the tapped row so the
       *  drawer reads as a continuation of that row rather than a
       *  brand-new context. */}
      <Stack sx={{ px: 2, pt: 1, pb: 1.5 }} spacing={0.25}>
        <Typography
          variant="overline"
          sx={{ color: "text.secondary", letterSpacing: 0.5, lineHeight: 1.2 }}
        >
          {heroLabel}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            fontFeatureSettings: '"tnum" 1',
            lineHeight: 1.1
          }}
        >
          {formatUSD(heroValue)}
        </Typography>
      </Stack>

      <Divider />

      {/* Scrollable body — compact BalancesView drops its own hero, so
       *  the echo above carries the headline role. */}
      <Box sx={{ px: 2, py: 1.5, overflowY: "auto" }}>
        <BalancesView
          accounts={accounts}
          bots={bots}
          isAggregate={isAggregate}
          compact
        />
      </Box>

      <Divider />

      <Box sx={{ p: 1.5 }}>
        <Button
          fullWidth
          variant="text"
          color="inherit"
          onClick={onClose}
          sx={{ fontWeight: 500, py: 1 }}
        >
          Close
        </Button>
      </Box>
    </SwipeableDrawer>
  );
}

// ---- Helpers used by PositionsPanel for price-derived deltas -------------

/** Add `n` calendar days (signed) to an `yyyy-mm-dd` ISO date. */
function isoAddDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Monday 00:00 UTC of the current calendar week, as a ms timestamp.
 *  Same logic mirrors `mondayOfThisWeekUTC` in whb-data.ts (kept local
 *  here to avoid touching the export surface of that module). */
function mondayOfThisWeekUTCMs(now: Date = new Date()): number {
  const dow = now.getUTCDay(); // 0=Sun … 6=Sat
  const daysSinceMonday = (dow + 6) % 7;
  return Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() - daysSinceMonday,
    0,
    0,
    0,
    0
  );
}

/** Most-recent close on or before `isoDate` from an ascending series.
 *  Returns null when the series is empty or every quote post-dates the
 *  cutoff. Linear scan — series are short (≤ ~3y of daily quotes). */
function closeOnOrBefore(
  series: readonly { date: string; close: number }[] | undefined,
  isoDate: string
): number | null {
  if (!series || series.length === 0) return null;
  let last: number | null = null;
  for (const q of series) {
    if (q.date <= isoDate) {
      last = q.close;
    } else {
      break;
    }
  }
  return last;
}
